import { sql } from "drizzle-orm";
import { pgTable, text, varchar, timestamp, jsonb, boolean, index, integer, uniqueIndex, check, serial, pgEnum, date, bigint } from "drizzle-orm/pg-core";
import { relations } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Session storage table - Required for Replit Auth
export const sessions = pgTable(
  "sessions",
  {
    sid: varchar("sid").primaryKey(),
    sess: jsonb("sess").notNull(),
    expire: timestamp("expire").notNull(),
  },
  (table) => [index("IDX_session_expire").on(table.expire)],
);

// DFY tier enums
export const companyTierEnum = pgEnum("company_tier", ["self_serve", "dfy"]);
export const batchStatusEnum = pgEnum("batch_status", ["draft", "pending_approval", "approved", "published"]);
// DFY multi-source generation source (defined here, above pillar_posts, so pillar_posts.source can reference it).
export const dfySourceEnum = pgEnum("dfy_source", ["content", "news", "repurposing"]);

// Users table - Updated for Replit Auth + member platform
export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: varchar("email").unique(),
  firstName: varchar("first_name"),
  lastName: varchar("last_name"),
  profileImageUrl: varchar("profile_image_url"),
  passwordHash: text("password_hash"),
  emailVerified: boolean("email_verified").default(false),
  role: varchar("role").default("user"), // 'user' | 'admin'
  status: varchar("status").default("pending"), // 'pending' | 'approved' - Waitlist system
  isOnboarded: boolean("is_onboarded").default(false),
  uploadToken: varchar("upload_token").unique().default(sql`gen_random_uuid()`), // Personal upload token for anonymous photo uploads
  // Payment and subscription fields
  subscriptionStatus: varchar("subscription_status").default("active"), // 'trial', 'active', 'inactive', 'expired', 'cancelled'
  subscriptionTier: varchar("subscription_tier").default("basic"), // 'basic', 'premium', 'enterprise'
  subscriptionExpiresAt: timestamp("subscription_expires_at"),
  lastPaymentDate: timestamp("last_payment_date"),
  paymentMethod: jsonb("payment_method").default({}), // Payment method details
  accessEnabled: boolean("access_enabled").default(true), // Admin can manually enable/disable access
  // Explicit app-level access state (onboarding overhaul Phase 3). Authoritative input for the
  // server-side access gate (server/services/accessControl.ts + server/middleware/requireAccess.ts).
  // Distinct from subscriptionStatus, which mirrors the Stripe subscription LIFECYCLE in Phase 4;
  // accessState is the app's derived ENTITLEMENT decision. Nullable during the expand/contract
  // rollout: NULL = a legacy row not yet backfilled → the gate derives access from subscriptionStatus
  // (grandfathered). New self-serve users get 'pending_payment' set explicitly at register — NOT via a
  // column default, which would re-introduce the "everyone active" landmine on any un-upgraded write
  // path. 'trial'/'active' are set ONLY by a verified Stripe webhook or an admin. See migration 0013
  // and ACCESS_STATES below.
  accessState: varchar("access_state").default("pending_payment"), // pending_payment | trial | active | expired | disabled | null(legacy)
  // NOTE: column default added in migration 0015 (SET DEFAULT 'pending_payment') so any insert that omits
  // access_state can't fall into the legacy subscriptionStatus="active" grandfather. 'active'/'trial' are
  // still only granted by a verified Stripe webhook or an admin. The NOT NULL contract step (item 30)
  // stays deferred to Phase 5 so the legacy/null tolerant-read path remains testable in the interim.
  // Stripe billing identity (Phase 4). `users` is the canonical owner; company refs derive via userId.
  // Unique so a Stripe customer/subscription maps to exactly one app user (Postgres unique permits many
  // NULLs, so unset rows don't collide). Set only by the verified Stripe webhook / checkout flow.
  stripeCustomerId: varchar("stripe_customer_id").unique(),
  stripeSubscriptionId: varchar("stripe_subscription_id").unique(),
  // event.created of the last subscription-mutating Stripe event applied — used to ignore stale /
  // out-of-order events (item 25) so an old update can't overwrite a newer cancellation/expiry.
  stripeEventCreatedAt: timestamp("stripe_event_created_at"),
  deletedAt: timestamp("deleted_at"), // Soft delete timestamp - null means not deleted
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Access states for the Phase-3 server-side access gate. Keep in sync with the users.access_state
// column above, migration 0013, and server/services/accessControl.ts.
//   pending_payment — self-serve default; registered + verified but no Stripe-backed entitlement yet.
//   trial           — card on file, inside the 7-day trial window (subscriptionExpiresAt = trial end).
//   active          — paying subscriber.
//   expired         — trial/subscription lapsed.
//   disabled        — hard off (legacy accessEnabled=false also blocks regardless of state).
// NULL (not in this list) = legacy row not yet backfilled → gate derives from subscriptionStatus.
export const ACCESS_STATES = ["pending_payment", "trial", "active", "expired", "disabled"] as const;
export type AccessState = (typeof ACCESS_STATES)[number];

// Companies table - for company intelligence phase
export const companies = pgTable("companies", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  description: text("description"), // optional
  industry: text("industry").notNull(),
  businessOverview: text("business_overview"), // Optional - wird durch AirTable-Automation gefüllt
  targetAudience: text("target_audience"), // Optional - wird später durch AirTable-Automation gefüllt
  valueProposition: text("value_proposition"), // Optional - wird später durch AirTable-Automation gefüllt
  linkedinProfile: text("linkedin_profile").notNull(),
  // Contact person fields (required)
  contactFirstName: text("contact_first_name").notNull(),
  contactLastName: text("contact_last_name").notNull(),
  contactEmail: text("contact_email").notNull(),
  contactPhone: text("contact_phone").notNull(),
  // Company details (required)
  website: text("website").notNull(),
  address: text("address").notNull(),
  // Optional fields
  logoUrl: text("logo_url"),
  // Password management
  passwordHash: text("password_hash"),
  passwordUpdatedAt: timestamp("password_updated_at"),
  // Account settings
  accountSettings: jsonb("account_settings").default({}),
  // Late.dev Integration (deprecated - use lateProfiles table)
  lateProfileId: text("late_profile_id"), // DEPRECATED: Use lateProfiles table
  lateProfileName: text("late_profile_name"), // DEPRECATED: Use lateProfiles table
  // A-Team Briefing - Content DNA fields
  // Section 1: Brand Voice (4 slider pairs, 0-100 scale)
  brandVoiceTones: jsonb("brand_voice_tones").default({}), // { professional_casual: 30, direct_empathetic: 70, factual_emotional: 50, formal_conversational: 60 }
  // Section 2: Example Posts
  examplePosts: jsonb("example_posts").default({}), // { linkedin: [{ url: "...", text: "..." }], instagram: [...], facebook: [...], youtube: [...] }
  // Section 3: Industry & Interests
  specificNiche: text("specific_niche"), // e.g., "Boutique-Hotels in Bergregionen"
  topicsOfInterest: text("topics_of_interest").array().default(sql`ARRAY[]::text[]`), // ['tools_tech', 'marketing', 'social_trends']
  newsletters: text("newsletters").array().default(sql`ARRAY[]::text[]`), // ["Marketing Week", "TechCrunch", "Branchenverband XY"]
  topicsToAvoid: text("topics_to_avoid").array().default(sql`ARRAY[]::text[]`), // ['politics', 'religion', 'controversial']
  topicsToAvoidOther: text("topics_to_avoid_other"), // free text for "other"
  // Section 4: Content Strategy
  postingFrequency: jsonb("posting_frequency").default({}), // { linkedin: 3, instagram: 5, facebook: 2, youtube: 1 }
  contentMix: jsonb("content_mix").default({}), // { educational: 40, thoughtLeadership: 30, behindTheScenes: 20, promotional: 10 } (must sum to 100)
  evergreenTopics: text("evergreen_topics").array().default(sql`ARRAY[]::text[]`), // ["Effiziente Team-Führung", "Marketing-Fehler", "KI-Integration"]
  // Brand Voice Summary (filled by N8N, admin-only)
  brandVoiceSummary: text("brand_voice_summary"), // AI-generated summary, only visible in admin dashboard
  // Meta fields
  contentDnaCompleted: boolean("content_dna_completed").default(false), // true when briefing is complete
  contentDnaUpdatedAt: timestamp("content_dna_updated_at"), // last update timestamp
  // Marketing Strategy Add-on
  hasStrategyAddon: boolean("has_strategy_addon").default(false), // DEPRECATED 2026-09: addon gate removed, column unused; drop in a later migration
  strategyGeneratedAt: timestamp("strategy_generated_at"), // When strategy was generated by N8N
  // Strategy Foundation User Input (filled before AI generation)
  customerMainProblem: text("customer_main_problem"), // User input: main problem customers face
  customerEnemy: text("customer_enemy"), // User input: the "enemy" or obstacle customers fight
  contrarianPov: text("contrarian_pov"), // User input: contrarian point of view
  // DFY tier fields
  tier: companyTierEnum("tier").notNull().default("self_serve"),
  dfyActivatedAt: timestamp("dfy_activated_at"),
  clientTelegramChatId: text("client_telegram_chat_id"),
  // Onboarding service preferences (Step 2 of the onboarding wizard) — previously collected but discarded
  services: text("services").array().default(sql`ARRAY[]::text[]`), // selected services
  marketingGoals: text("marketing_goals").array().default(sql`ARRAY[]::text[]`), // selected marketing goals
  // Signal-Room client-lane mapping (phase 0). client_id = the Signal-Room creator id — the
  // IMMUTABLE external identity the exporter keys on; slug is display-only. Changed ONLY via the
  // admin mapping PATCH, which writes a signal_room_mapping_events audit row. import_enabled is
  // the per-company rollout allowlist flag (PLAN 7c) UNDER the global SIGNAL_ROOM_IMPORT_ENABLED
  // kill switch — both must be on for an import to write.
  signalRoomClientId: text("signal_room_client_id"),
  signalRoomSlug: text("signal_room_slug"),
  signalRoomImportEnabled: boolean("signal_room_import_enabled").notNull().default(false),
  // Brand kit (Block 2c). Durable monotonic mutation token — the SINGLE `expectedVersion` optimistic
  // lock for every brand-kit mutation (PUT/DELETE/logo/adopt) AND the ordering `version` Signal Room
  // reads. Lives on companies (not company_brand) so it survives a kit reset/recreate and a stale tab
  // from before the reset can never act on the new kit. Incremented by EVERY mutation including reset.
  brandVersion: integer("brand_version").notNull().default(0),
  // Onboarding v2 (migration 0027, plan Rev. 3). instagramHandle is normalized (lowercase, no @/URL).
  // checklistDismissedAt NULL = the check-in is the start page. houseProfileRev bumps on every
  // houseProfile change so a strategy job can tell which revision it used. profilcheckGen versions
  // the Profilcheck baseline idempotency key across handle changes. All server-owned except via the
  // dedicated onboarding endpoints (see insertCompanySchema omit list).
  instagramHandle: text("instagram_handle"),
  checklistDismissedAt: timestamp("checklist_dismissed_at"),
  dfyActivationRequestedAt: timestamp("dfy_activation_requested_at"),
  houseProfile: jsonb("house_profile"), // { guestsLove, offers, season, story }
  houseProfileRev: integer("house_profile_rev").notNull().default(0),
  profilcheckGen: integer("profilcheck_gen").notNull().default(0),
  // DFY for hotels (migration 0028, shared/dfyChannel.ts). dfyProfile picks the n8n content engine
  // (agency = live B2B engine, hotel = "DFY Hotel"); platform/ratio NULL = profile default. Admin-only.
  dfyProfile: text("dfy_profile").notNull().default("agency"),
  dfyPlatform: text("dfy_platform"),
  dfyImageAspectRatio: text("dfy_image_aspect_ratio"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => [
  // One company per Signal-Room client id (imports reject ambiguity at the DB, not just in code).
  uniqueIndex("uq_companies_signal_room_client_id")
    .on(t.signalRoomClientId)
    .where(sql`signal_room_client_id IS NOT NULL`),
]);
// NOTE: no DB unique index on companies.user_id (yet). Existing test fixtures intentionally create
// two companies per user (DfyCo + SsCo), and real-customer duplicates are ~nil. The one-company-per-user
// race is instead handled by a per-user advisory lock in onboardingService.finalizeOnboarding. The
// unique index is a deferred hardening once those fixtures are refactored.

// Onboarding enrichment jobs — durable record of the post-finalization N8N AI enrichment
// dispatch, so the UI/ops see pending/running/succeeded/failed instead of blind polling.
export const onboardingJobs = pgTable("onboarding_jobs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: varchar("company_id").notNull().references(() => companies.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  status: varchar("status").notNull().default("pending"), // 'pending' | 'running' | 'succeeded' | 'failed'
  attempts: integer("attempts").notNull().default(0),
  lastError: text("last_error"),
  dispatchedAt: timestamp("dispatched_at"),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  // One enrichment job per company (idempotent re-finalize upserts the same row).
  companyUnique: uniqueIndex("onboarding_jobs_company_unique").on(t.companyId),
}));

// Onboarding background tasks (plan Rev. 3, item 11): one durable state machine for enrichment,
// strategy, strategy mail and Profilcheck work. Transitions: pending → running (claim with a fresh
// leaseToken) → succeeded | pending (retry with nextAttemptAt) | superseded | dead. There is no
// retryable `failed`. Every completion write is fenced by `id AND lease_token`.
export const ONBOARDING_TASK_STATUSES = ["pending", "running", "succeeded", "superseded", "dead"] as const;
export type OnboardingTaskStatus = (typeof ONBOARDING_TASK_STATUSES)[number];
export const onboardingTasks = pgTable("onboarding_tasks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: varchar("company_id").notNull().references(() => companies.id, { onDelete: "cascade" }),
  kind: varchar("kind", { length: 40 }).notNull(), // enrichment | strategy | strategy_update | strategy_mail | profilcheck
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  attempts: integer("attempts").notNull().default(0), // business failures only
  transportErrors: integer("transport_errors").notNull().default(0),
  nextAttemptAt: timestamp("next_attempt_at").notNull().defaultNow(),
  leaseUntil: timestamp("lease_until"),
  leaseToken: varchar("lease_token"),
  deadlineAt: timestamp("deadline_at"),
  idempotencyKey: text("idempotency_key").notNull(),
  inputRev: integer("input_rev"),
  ref: text("ref"),
  lastError: text("last_error"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  completedAt: timestamp("completed_at"),
}, (t) => [
  uniqueIndex("uq_onboarding_tasks_idempotency_key").on(t.idempotencyKey),
  uniqueIndex("uq_onboarding_tasks_open_per_kind").on(t.companyId, t.kind).where(sql`status IN ('pending', 'running')`),
  index("idx_onboarding_tasks_due").on(t.status, t.nextAttemptAt),
]);
export type OnboardingTask = typeof onboardingTasks.$inferSelect;

// Stripe webhook event ledger (Phase 4) — idempotency + audit. We record every event we accept,
// keyed by the Stripe event id (natural PK), so a replay is a no-op (insert-on-conflict-do-nothing).
// The ordering guard (ignore stale events, item 25) lives on users.stripeEventCreatedAt; this table is
// the durable record of what was processed.
export const stripeEvents = pgTable("stripe_events", {
  id: varchar("id").primaryKey(), // Stripe event id (evt_...) — natural PK = idempotency key
  type: varchar("type").notNull(), // e.g. checkout.session.completed
  eventCreated: timestamp("event_created").notNull(), // Stripe event.created (UTC instant)
  userId: varchar("user_id").references(() => users.id, { onDelete: "set null" }), // correlated app user, if known
  payload: jsonb("payload"), // raw event for audit / replay
  receivedAt: timestamp("received_at").defaultNow(),
  // Phase 5 item 1: NULL = recorded-but-not-yet-applied (crash window OR parked under mutation-off). Set
  // only after a clean apply. A redelivery is acked-as-duplicate ONLY when this is non-null; the replay
  // sweep (item 3) applies every applied_at IS NULL row once mutation is enabled.
  appliedAt: timestamp("applied_at"),
});

// Weekly Batches table - DFY weekly content batch per company
export const weeklyBatches = pgTable("weekly_batches", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: varchar("company_id").notNull().references(() => companies.id, { onDelete: "cascade" }),
  weekIso: text("week_iso").notNull(), // e.g. "2026-W21"
  status: batchStatusEnum("status").notNull().default("draft"),
  openedAt: timestamp("opened_at").defaultNow().notNull(),
  approvedAt: timestamp("approved_at"),
  publishedAt: timestamp("published_at"),
  metricsJson: jsonb("metrics_json").default({}),
  publishError: text("publish_error"),
  // DFY Phase 1c (hardening): make batches cycle/revision-aware so reruns can create a
  // fresh batch for the same (company, week). `supersededAt` is a LOCAL marker — the
  // active-only partial unique below needs a column on THIS table (can't reference a join).
  cycleId: varchar("cycle_id"),
  revision: integer("revision").notNull().default(1),
  supersededAt: timestamp("superseded_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (t) => ({
  // Was an UNCONDITIONAL unique (blocked reruns). Now active-only: ≤1 non-superseded
  // batch per (company, week); superseded revisions coexist.
  companyWeekActiveUnique: uniqueIndex("weekly_batches_company_week_active_unique")
    .on(t.companyId, t.weekIso)
    .where(sql`superseded_at IS NULL`),
}));

// Agent Service Accounts table - API tokens for Lina and other agents
export const agentServiceAccounts = pgTable("agent_service_accounts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  tokenHash: text("token_hash").notNull().unique(),
  companyScope: varchar("company_scope"),
  permissions: jsonb("permissions").notNull().default(["batches:read", "batches:write"]),
  createdBy: varchar("created_by").references(() => users.id),
  lastUsedAt: timestamp("last_used_at"),
  revokedAt: timestamp("revoked_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

// Late.dev Profiles table - Multi-tenant Late.dev profile management
export const lateProfiles = pgTable("late_profiles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: varchar("company_id").notNull().unique().references(() => companies.id, { onDelete: "cascade" }),
  lateProfileId: text("late_profile_id").notNull(), // Late.dev Profile ID from API
  displayName: text("display_name").notNull(), // Profile name (e.g., "Sumo2Day-PROD")
  status: text("status").notNull().default("provisioning"), // "provisioning", "ready", "error"
  errorMessage: text("error_message"), // Error details if status="error"
  lastSyncAt: timestamp("last_sync_at"), // Last time accounts were synced
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  uniqueIndex("late_profiles_company_id_idx").on(table.companyId),
]);

// Automations table - tracking 6-phase automation progress
export const automations = pgTable("automations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  phase: text("phase").notNull(), // "company_intelligence", "post_intelligence", etc.
  status: text("status").default("pending"), // "pending", "in_progress", "completed", "failed"
  settings: jsonb("settings"), // Phase-specific settings
  results: jsonb("results"), // Phase results/output
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// N8N Workflows table - storing N8N workflow connections
export const n8nWorkflows = pgTable("n8n_workflows", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  workflowName: text("workflow_name").notNull(),
  n8nWorkflowId: text("n8n_workflow_id"), // N8N's internal workflow ID
  workflowUrl: text("workflow_url"),
  webhookUrl: text("webhook_url"),
  webhookSecret: varchar("webhook_secret").default(sql`gen_random_uuid()`), // Secret for webhook authentication
  status: text("status").default("active"), // "active", "paused", "error"
  lastTriggered: timestamp("last_triggered"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Content Items table - for content management
export const contentItems = pgTable("content_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  description: text("description"),
  category: text("category").notNull(), // "KI Consulting", "App Posting", "Evergreen", etc.
  source: text("source").default("manual"), // "manual", "ai_generated", "imported"
  platform: text("platform"), // primary platform for this content
  url: text("url"), // external URL to original content
  isUsed: boolean("is_used").default(false),
  usageCount: integer("usage_count").default(0), // how many times this content was used
  platforms: jsonb("platforms").default([]), // ["facebook", "linkedin", "instagram", "google_business"]
  usedAt: timestamp("used_at"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Social Media Goals table - for platform-specific goals
export const socialMediaGoals = pgTable("social_media_goals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  platform: text("platform").notNull(), // "facebook", "instagram", "linkedin", "google_business"
  goalType: text("goal_type").notNull(), // "followers", "likes", "comments", "shares", "views", "engagement"
  targetValue: integer("target_value").notNull(), // numerical target (e.g., 1000 followers)
  currentValue: integer("current_value").default(0), // current progress toward goal
  deadline: timestamp("deadline").notNull(), // when the goal should be achieved
  description: text("description"), // optional additional details
  status: text("status").default("active"), // "active", "completed", "paused"
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Content Uploads table - for photo uploads with messages and review system
export const contentUploads = pgTable("content_uploads", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id, { onDelete: "cascade" }), // nullable for anonymous uploads
  uploadToken: varchar("upload_token"), // Token used for upload (for linking anonymous uploads)
  fileName: text("file_name").notNull(),
  originalName: text("original_name").notNull(),
  filePath: text("file_path").notNull(),
  fileSize: integer("file_size").notNull(),
  mimeType: text("mime_type").notNull(),
  message: text("message"), // User message attached to photo
  pillarName: text("pillar_name"), // content pillar (theme) the photo belongs to; NULL = no theme (migration 0027)
  uploadMethod: text("upload_method").notNull(), // "direct", "email", "token_share"
  status: text("status").default("pending"), // "pending", "approved", "rejected"
  reviewedBy: varchar("reviewed_by").references(() => users.id), // Admin who reviewed
  reviewedAt: timestamp("reviewed_at"),
  reviewNotes: text("review_notes"), // Admin notes for rejection/approval
  isProcessed: boolean("is_processed").default(false), // Whether content was generated
  // N8N Integration fields
  n8nProcessingStatus: text("n8n_processing_status").default("not_sent"), // "not_sent", "sent", "processing", "completed", "failed"
  n8nWebhookSentAt: timestamp("n8n_webhook_sent_at"), // When webhook was sent to N8N
  n8nResponseReceivedAt: timestamp("n8n_response_received_at"), // When N8N completed processing
  generatedPostContent: jsonb("generated_post_content"), // N8N generated post content and metadata
  n8nWorkflowId: text("n8n_workflow_id"), // N8N workflow execution ID for tracking
  n8nErrorMessage: text("n8n_error_message"), // Error message if N8N processing failed
  // Hotel-Edition C-5 (migration 0030): per-file client id; UNIQUE (user_id, client_upload_id) so a retried
  // upload returns the same row instead of a duplicate. NULL for uploads from older clients.
  clientUploadId: text("client_upload_id"),
  // Hotel-Edition review he-c R1 (migration 0031): hex SHA-256 of the bytes of a hotel upload with a
  // clientUploadId — a retry under the same id must carry the same bytes, else 409. NULL for all other rows.
  contentSha256: text("content_sha256"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Visual Brand Requests table - for AI-generated brand visuals (person/product mode)
export const visualBrandRequests = pgTable("visual_brand_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  companyId: varchar("company_id").notNull().references(() => companies.id, { onDelete: "cascade" }),
  mainPhotoPath: text("main_photo_path").notNull(),
  mainPhotoName: text("main_photo_name").notNull(),
  referencePhotoPath: text("reference_photo_path"),
  referencePhotoName: text("reference_photo_name"),
  mode: text("mode").notNull(), // "person" | "product"
  message: text("message"),
  status: text("status").default("submitted"), // "submitted", "processing", "ready", "failed"
  n8nJobId: text("n8n_job_id"),
  n8nError: text("n8n_error"),
  resultImages: jsonb("result_images").default([]), // Array of { id, filePath, mimeType, createdAt }
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Brand Visual Videos table — Brand Visuals → Captioned Video, Slice 1. One row per rendered
// (request, source image, platform) captioned video, produced from a visual_brand_requests result
// image. Idempotency is scoped to the REQUEST (not globally on sourceImageId+platform): the
// visual-brand webhook falls back to array-index image ids like "0"/"1" (not globally unique), so a
// global unique would collide across different requests/tenants.
export const brandVisualVideos = pgTable("brand_visual_videos", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  requestId: varchar("request_id").notNull().references(() => visualBrandRequests.id, { onDelete: "cascade" }),
  companyId: varchar("company_id").notNull().references(() => companies.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  sourceImageId: text("source_image_id").notNull(), // id within visualBrandRequests.resultImages
  platform: text("platform").notNull(),
  aspect: text("aspect").notNull(),
  // Immutable render-input snapshot, captured at request time. A later task makes the visual-brand
  // callback merge-UPDATE resultImages elements in place; without a frozen copy here, a callback
  // arriving after the user requests a video could change the source image path/script before the
  // async renderer reads it, producing the wrong video. These columns are exactly what to render.
  sourceImagePath: text("source_image_path").notNull(),
  sourceImageContentType: text("source_image_content_type"),
  scriptSnapshot: text("script_snapshot").notNull(),
  status: text("status").notNull().default("requested"), // "requested" | "processing" | "ready" | "failed"
  videoPath: text("video_path"), // /objects/... S3 path, set when ready
  attempts: integer("attempts").notNull().default(0),
  nextAttemptAt: timestamp("next_attempt_at"), // retry backoff gate
  leaseOwner: varchar("lease_owner"),
  leasedAt: timestamp("leased_at"),
  lastError: text("last_error"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  // Idempotency scoped to the request — see table comment above.
  uniqueIndex("uq_brand_video_request_image_platform").on(table.requestId, table.sourceImageId, table.platform),
  index("idx_brand_video_status_next").on(table.status, table.nextAttemptAt), // drainer poll
  index("idx_brand_video_status").on(table.status), // global concurrency count
  index("idx_brand_video_request").on(table.requestId),
  index("idx_brand_video_company").on(table.companyId),
]);

export const insertBrandVisualVideoSchema = createInsertSchema(brandVisualVideos).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertBrandVisualVideo = z.infer<typeof insertBrandVisualVideoSchema>;
export type BrandVisualVideo = typeof brandVisualVideos.$inferSelect;

// Hotel lead-magnet funnel buffer. Sits between the n8n capture webhook (which enqueues)
// and the Mac-side drain (which lists, delivers, deletes). It lives here because this host
// is always up and the Mac is not — it sleeps on idle, so a lead arriving overnight has
// nowhere to land otherwise. Previously an n8n Data Table, moved 2026-07-27 after the
// drain's 2-minute poll exhausted the n8n Cloud execution quota and took DFY down with it.
//
// `payload` is OPAQUE and byte-preserved: the drain HMAC-signs these exact bytes to satisfy
// the dashboard's intake-auth, so the app must never parse, re-serialize, or normalize it.
export const hotelLeadQueue = pgTable("hotel_lead_queue", {
  submissionId: varchar("submission_id").primaryKey(), // minted by the n8n capture workflow; idempotency key
  payload: text("payload").notNull(),
  attempts: integer("attempts").notNull().default(0),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => [
  index("idx_hotel_lead_queue_created").on(table.createdAt), // drain reads oldest-first
]);

export type HotelLeadQueueRow = typeof hotelLeadQueue.$inferSelect;

// Branded short links for the hotel report emails: /r/<token> 302-redirects to the S3
// presigned URL. Exists for deliverability — a raw ~500-char amazonaws.com link in a thin
// email is spam-heuristic bait (observed: iCloud spam-foldered the first real report,
// 2026-08-04), while a short link on the sender's own domain is not. The Mac worker
// registers the link (secret-gated) right before sending; rows outlive their `expiresAt`
// harmlessly (redirect answers 410) and are deleted with the lead on a DSGVO erasure via
// the submission_id index.
export const hotelReportLinks = pgTable("hotel_report_links", {
  token: varchar("token").primaryKey(), // 128-bit base64url, minted server-side
  submissionId: varchar("submission_id").notNull(),
  url: text("url").notNull(), // the S3 presigned GET; opaque, never parsed
  expiresAt: timestamp("expires_at").notNull(), // mirrors the presign TTL
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => [
  index("idx_hotel_report_links_submission").on(table.submissionId), // DSGVO erasure lookup
]);

export type HotelReportLinkRow = typeof hotelReportLinks.$inferSelect;

// Generated Posts table - for platform-specific N8N generated social media posts
export const generatedPosts = pgTable("generated_posts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  contentUploadId: varchar("content_upload_id").references(() => contentUploads.id, { onDelete: "cascade" }), // nullable for industry_news posts
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  companyId: varchar("company_id").references(() => companies.id, { onDelete: "cascade" }), // for multi-tenant industry news
  sourceType: text("source_type"), // "content_upload", "industry_news", "evergreen_content"
  sourceId: varchar("source_id"), // ID of source (news.id, evergreen.id, etc.)
  platform: text("platform").notNull(), // "facebook", "instagram", "linkedin", "google_business", "twitter"
  postTitle: text("post_title"),
  postContent: text("post_content").notNull(), // The generated post text
  hashtags: text("hashtags").array(), // Array of hashtags for this platform
  imageContent: text("image_content"), // Base64 image data or processed image URL for this platform
  platformSpecificData: jsonb("platform_specific_data").default({}), // Platform-specific formatting, character limits, etc.
  // Approval and posting workflow
  approvalStatus: text("approval_status").default("pending"), // "pending", "approved", "rejected"
  approvedBy: varchar("approved_by").references(() => users.id), // Who approved this post
  approvedAt: timestamp("approved_at"),
  approvalNotes: text("approval_notes"), // Notes from the approver
  // Posting status and tracking
  postingStatus: text("posting_status").default("draft"), // "draft", "scheduled", "posting", "posted", "failed"
  scheduledFor: timestamp("scheduled_for"), // When to post (if scheduled)
  postedAt: timestamp("posted_at"), // When it was actually posted
  platformPostId: text("platform_post_id"), // ID from the social media platform after posting
  postingError: text("posting_error"), // Error message if posting failed
  // Late per-post metrics, written by the analytics ingest sweep. INVARIANT: rows
  // sharing a latePostId (incl. pillar_posts rows) carry identical copies — consumers
  // aggregating across rows must dedup by latePostId (see overviewService).
  engagementData: jsonb("engagement_data").default({}),
  // Auto-posting settings
  isAutoPostEnabled: boolean("is_auto_post_enabled").default(false), // User setting per platform/post
  autoPostedAt: timestamp("auto_posted_at"), // When auto-posting occurred
  // Late.dev Integration
  latePostId: text("late_post_id"), // Late.dev post ID after publishing
  platformUrls: jsonb("platform_urls").default({}), // URLs to posts on each platform { linkedin: "https://...", facebook: "https://..." }
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  // CHECK constraint: Exactly one source must be set (contentUploadId OR sourceId)
  check("generated_posts_source_integrity", 
    sql`(${table.contentUploadId} IS NOT NULL AND ${table.sourceId} IS NULL) OR (${table.contentUploadId} IS NULL AND ${table.sourceId} IS NOT NULL)`
  ),
  // Index for efficient queries by upload and platform
  uniqueIndex("generated_posts_upload_platform_unique").on(table.contentUploadId, table.platform),
  // Index for user's posts by platform
  index("generated_posts_user_platform_idx").on(table.userId, table.platform),
  // Index for pending approvals
  index("generated_posts_approval_status_idx").on(table.approvalStatus),
  // Index for posting status queries
  index("generated_posts_posting_status_idx").on(table.postingStatus),
  // Index for source-based lookups (industry news, evergreen, etc.)
  index("generated_posts_source_idx").on(table.sourceType, table.sourceId),
]);

// Evergreen Content table - for AI-generated evergreen content catalog
export const evergreenContent = pgTable("evergreen_content", {
  id: serial("id").primaryKey(),
  companyId: varchar("company_id", { length: 255 }).notNull().references(() => companies.id, { onDelete: "cascade" }),
  catalogId: varchar("catalog_id", { length: 255 }).notNull(),
  postNumber: integer("post_number").notNull(),
  topic: varchar("topic", { length: 255 }).notNull(),
  angle: text("angle").notNull(),
  contentType: varchar("content_type", { length: 50 }).notNull(),
  platforms: jsonb("platforms").notNull(),
  reason: text("reason").notNull(),
  relevanceScore: integer("relevance_score").default(5),
  estimatedEngagement: varchar("estimated_engagement", { length: 20 }),
  status: varchar("status", { length: 20 }).default("unused"),
  usedCount: integer("used_count").default(0),
  lastUsedDate: timestamp("last_used_date"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  check("valid_content_type", sql`${table.contentType} IN ('tip', 'behind-scenes', 'education', 'story')`),
  check("valid_status", sql`${table.status} IN ('unused', 'used', 'archived')`),
  check("valid_relevance_score", sql`${table.relevanceScore} >= 1 AND ${table.relevanceScore} <= 10`),
  index("idx_evergreen_company").on(table.companyId),
  index("idx_evergreen_status").on(table.status),
  index("idx_evergreen_catalog").on(table.catalogId),
  index("idx_evergreen_content_type").on(table.contentType),
]);

// Marketing Strategies table - for AI-generated marketing strategy from N8N
export const marketingStrategies = pgTable("marketing_strategies", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: varchar("company_id").notNull().unique().references(() => companies.id, { onDelete: "cascade" }),
  // Brand Story / Manifesto
  manifesto: text("manifesto"), // Emotional 5-paragraph brand story
  // Strategic Foundation
  enemyName: text("enemy_name"),
  enemyDescription: text("enemy_description"),
  povStatement: text("pov_statement"),
  positioningStatement: text("positioning_statement"),
  // Core Messages (Before/Bridge/After transformations)
  messages: jsonb("messages").default([]), // [{ title, before, bridge, after }]
  // Content Pillars (array of pillar objects)
  contentPillars: jsonb("content_pillars").default([]), // [{ name, type, themes, primaryBuyerStage, why }]
  // Problem Stack
  surfaceProblem: text("surface_problem"),
  rootCause: text("root_cause"),
  businessImpact: text("business_impact"),
  emotionalImpact: text("emotional_impact"),
  // Metadata
  generatedAt: timestamp("generated_at").defaultNow(),
  lastEditedAt: timestamp("last_edited_at"),
  n8nSyncStatus: text("n8n_sync_status").default("synced"), // "synced", "pending", "error"
  n8nLastSyncAt: timestamp("n8n_last_sync_at"),
  // Optimistic version (migration 0027): every write bumps it; a strategy callback only writes when the
  // version still equals the one the generation was dispatched against (plan Rev. 3, item 15).
  version: integer("version").notNull().default(1),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_marketing_strategy_company").on(table.companyId),
]);

// Industry News table - for AI-scraped industry news with generated posts
export const industryNews = pgTable("industry_news", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: varchar("company_id").notNull().references(() => companies.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  summary: text("summary"),
  imageUrl: text("image_url"), // Stored in /uploads/industry-news/
  relevanceScore: integer("relevance_score"), // 0-100 from n8n scoring
  sourceUrl: text("source_url"), // Original news article URL
  sourceName: text("source_name"), // e.g., "NTT", "TechCrunch"
  status: text("status").default("pending"), // "pending", "approved", "rejected"
  reviewedBy: varchar("reviewed_by").references(() => users.id),
  reviewedAt: timestamp("reviewed_at"),
  reviewNotes: text("review_notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_industry_news_company").on(table.companyId),
  index("idx_industry_news_status").on(table.status),
  index("idx_industry_news_relevance").on(table.relevanceScore),
]);

// Napkin Wisdom table - for AI-generated evergreen business insight posts with napkin sketch images
export const napkinWisdom = pgTable("napkin_wisdom", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: varchar("company_id").notNull().references(() => companies.id, { onDelete: "cascade" }),
  title: text("title").notNull(),
  summary: text("summary"),
  imageUrl: text("image_url"), // Napkin sketch image URL
  videoUrl: text("video_url"), // Branded video URL (Miragic + FFmpeg overlay)
  thumbnailUrl: text("thumbnail_url"), // Video thumbnail extracted from first frame
  episodeNumber: integer("episode_number"), // Episode counter for "Napkin Wisdom #X" branding
  relevanceScore: integer("relevance_score"), // 0-100 from n8n scoring
  sourceUrl: text("source_url"), // Empty for Napkin Wisdom
  sourceName: text("source_name"), // "Napkin Wisdom"
  publishedDate: text("published_date"), // Date from n8n payload
  status: text("status").default("pending"), // "pending", "approved", "rejected"
  reviewedBy: varchar("reviewed_by").references(() => users.id),
  reviewedAt: timestamp("reviewed_at"),
  reviewNotes: text("review_notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_napkin_wisdom_company").on(table.companyId),
  index("idx_napkin_wisdom_status").on(table.status),
  index("idx_napkin_wisdom_relevance").on(table.relevanceScore),
]);

// Repurposing Content table - for YouTube/Article URL to Social Media Post generation
export const repurposingContent = pgTable("repurposing_content", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: varchar("company_id").notNull().references(() => companies.id, { onDelete: "cascade" }),
  url: text("url").notNull(), // YouTube URL or Article URL
  title: text("title").notNull(), // Video/Article title from N8N
  summary: text("summary"), // AI-generated summary of content
  sourceType: text("source_type").notNull(), // "youtube", "article", "blog"
  thumbnailUrl: text("thumbnail_url"), // Optional thumbnail from YouTube or article
  status: text("status").default("pending"), // "pending", "approved", "rejected"
  reviewedBy: varchar("reviewed_by").references(() => users.id),
  reviewedAt: timestamp("reviewed_at"),
  reviewNotes: text("review_notes"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_repurposing_company").on(table.companyId),
  index("idx_repurposing_status").on(table.status),
  index("idx_repurposing_source_type").on(table.sourceType),
]);

// OAuth Apps table - for multi-app architecture management
export const oauthApps = pgTable("oauth_apps", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  platform: text("platform").notNull(), // "facebook", "instagram", "linkedin", "google_business"
  appName: text("app_name").notNull(), // "LinkedIn Lina 1", "LinkedIn Lina 2", etc.
  clientId: text("client_id").notNull(),
  clientSecret: text("client_secret").notNull(), // Encrypted in storage
  redirectUri: text("redirect_uri").notNull(),
  userCount: integer("user_count").default(0), // Current number of users assigned
  maxUsers: integer("max_users").default(25), // Maximum users per app (25-30)
  isActive: boolean("is_active").default(true),
  rateLimitStatus: jsonb("rate_limit_status").default({}), // Track rate limit usage
  lastRateLimitCheck: timestamp("last_rate_limit_check"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  // CHECK constraint to enforce max_users within 25-30 range for load balancing (per business requirements)
  check("oauth_apps_max_users_range", sql`${table.maxUsers} BETWEEN 25 AND 30`),
]);

// Social Media Connections table - for user platform OAuth connections
export const socialMediaConnections = pgTable("social_media_connections", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  platform: text("platform").notNull(), // "facebook", "instagram", "linkedin", "google_business"
  encryptedAccessToken: text("encrypted_access_token").notNull(), // Encrypted OAuth access token
  encryptedRefreshToken: text("encrypted_refresh_token"), // Encrypted OAuth refresh token (if available)
  tokenExpiry: timestamp("token_expiry"), // When the access token expires
  connectionStatus: boolean("connection_status").default(true), // Active/inactive connection
  assignedOauthAppId: varchar("assigned_oauth_app_id").notNull().references(() => oauthApps.id),
  connectionDate: timestamp("connection_date").defaultNow(),
  lastTokenRefresh: timestamp("last_token_refresh"),
  platformUserId: text("platform_user_id"), // User ID from the platform (for API calls)
  platformUsername: text("platform_username"), // Username on the platform
  permissions: jsonb("permissions").default([]), // Granted OAuth permissions/scopes
  connectionMetadata: jsonb("connection_metadata").default({}), // Additional platform-specific data
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  // UNIQUE constraint to ensure one connection per user per platform (critical for load balancing)
  uniqueIndex("social_media_connections_user_platform_unique").on(table.userId, table.platform),
]);

// Late.dev Social Accounts table - for Late.dev managed social media connections
export const lateSocialAccounts = pgTable("late_social_accounts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  lateAccountId: text("late_account_id"), // Account ID from Late.dev (nullable - populated after OAuth or first API call)
  platform: text("platform").notNull(), // "linkedin", "facebook", "instagram", "twitter", "bluesky", etc.
  platformUsername: text("platform_username"), // Username on the platform
  displayName: text("display_name"), // Display name from the platform
  isActive: boolean("is_active").default(true), // Whether connection is active
  tokenExpiresAt: timestamp("token_expires_at"), // When the token expires (if applicable)
  connectionMetadata: jsonb("connection_metadata").default({}), // Additional platform-specific data from Late.dev
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  // UNIQUE constraint to ensure one Late.dev account per user per platform
  uniqueIndex("late_social_accounts_user_platform_unique").on(table.userId, table.platform),
  // Index for efficient queries by Late.dev account ID
  index("late_social_accounts_late_account_id_idx").on(table.lateAccountId),
]);

// Preview Pages table - for personalized cold outreach landing pages
// NOTE: This table is intentionally standalone (no foreign keys) for future module extraction
export const previewPages = pgTable("preview_pages", {
  id: serial("id").primaryKey(),
  
  // Identification
  slug: varchar("slug", { length: 255 }).unique().notNull(),
  
  // Status & Lifecycle
  status: varchar("status", { length: 50 }).default("active").notNull(), // draft/active/expired/converted
  createdAt: timestamp("created_at").defaultNow().notNull(),
  expiresAt: timestamp("expires_at"),
  
  // Segment (for filtering/reporting)
  segment: varchar("segment", { length: 100 }), // 'hotel', 'restaurant', 'agency', etc.
  
  // Company data (generic, not industry-specific)
  companyName: varchar("company_name", { length: 255 }).notNull(),
  companyCity: varchar("company_city", { length: 255 }),
  companyWebsite: varchar("company_website", { length: 500 }),
  
  // Contact
  contactName: varchar("contact_name", { length: 255 }),
  contactEmail: varchar("contact_email", { length: 255 }),
  
  // Content
  photoUrls: jsonb("photo_urls").default([]),
  postInstagram: jsonb("post_instagram").default({}),
  postFacebook: jsonb("post_facebook").default({}),
  postLinkedin: jsonb("post_linkedin").default({}),
  
  // A-Team Comments
  linaComment: text("lina_comment"),
  maxComment: text("max_comment"),
  emmaComment: text("emma_comment"),
  alexComment: text("alex_comment"),
  
  // Tracking
  views: integer("views").default(0).notNull(),
  ctaClicks: integer("cta_clicks").default(0).notNull(),
  firstViewedAt: timestamp("first_viewed_at"),
  lastViewedAt: timestamp("last_viewed_at"),
  convertedAt: timestamp("converted_at"),
  
  // Campaign Tracking
  campaignId: varchar("campaign_id", { length: 100 }),
  utmSource: varchar("utm_source", { length: 100 }),
  utmMedium: varchar("utm_medium", { length: 100 }),
  utmCampaign: varchar("utm_campaign", { length: 100 }),
}, (table) => [
  index("idx_preview_slug").on(table.slug),
  index("idx_preview_segment").on(table.segment),
  index("idx_preview_status").on(table.status),
  index("idx_preview_campaign").on(table.campaignId),
]);

// Preview Page Events table - for detailed event tracking (GDPR compliant)
export const previewPageEvents = pgTable("preview_page_events", {
  id: serial("id").primaryKey(),
  slug: varchar("slug", { length: 255 }), // nullable for direct VSL visits
  eventType: varchar("event_type", { length: 50 }).notNull(), // 'page_view', 'video_click', 'cta_click', 'vsl_page_view', 'vsl_video_play'
  hashedIp: varchar("hashed_ip", { length: 64 }), // SHA-256 hash for duplicate detection
  userAgent: text("user_agent"),
  referrerSlug: varchar("referrer_slug", { length: 255 }), // tracks which preview page led to VSL
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_preview_events_slug").on(table.slug),
  index("idx_preview_events_type").on(table.eventType),
  index("idx_preview_events_created").on(table.createdAt),
]);

export const insertPreviewPageEventSchema = createInsertSchema(previewPageEvents).omit({ id: true, createdAt: true });
export type InsertPreviewPageEvent = z.infer<typeof insertPreviewPageEventSchema>;
export type PreviewPageEvent = typeof previewPageEvents.$inferSelect;

// Evergreen Catalogs table - stores AI-generated content catalogs from N8N
export const evergreenCatalogs = pgTable("evergreen_catalogs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: varchar("company_id").notNull().references(() => companies.id, { onDelete: "cascade" }),
  eventType: varchar("event_type", { length: 50 }).default("catalog_generated"),
  processingTime: text("processing_time"), // seconds as string to preserve decimals
  statistics: jsonb("statistics").default({}), // { total_posts, content_type_distribution, platform_distribution }
  metadata: jsonb("metadata").default({}), // { workflow_version, generation_source }
  receivedAt: timestamp("received_at").defaultNow().notNull(), // when N8N sent it
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_evergreen_catalogs_company").on(table.companyId),
  index("idx_evergreen_catalogs_created").on(table.createdAt),
]);

// Evergreen Catalog Posts table - individual posts within a catalog
export const evergreenCatalogPosts = pgTable("evergreen_catalog_posts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  catalogId: varchar("catalog_id").notNull().references(() => evergreenCatalogs.id, { onDelete: "cascade" }),
  number: integer("number").notNull(), // Post number within catalog (1-40)
  topic: text("topic").notNull(),
  angle: text("angle").notNull(),
  contentType: varchar("content_type", { length: 50 }).notNull(), // education, tip, behind-scenes, story, thought-leadership
  platforms: jsonb("platforms").default([]).notNull(), // ["linkedin", "facebook", "instagram"]
  hookPreview: text("hook_preview"),
  reason: text("reason"),
  relevanceScore: integer("relevance_score"), // 1-10
  estimatedEngagement: varchar("estimated_engagement", { length: 20 }), // high, medium, low
  isSelected: boolean("is_selected").default(false), // user selection for content creation
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_evergreen_posts_catalog").on(table.catalogId),
  index("idx_evergreen_posts_content_type").on(table.contentType),
  index("idx_evergreen_posts_selected").on(table.isSelected),
]);

// Evergreen Catalogs: Zod schemas and types
export const insertEvergreenCatalogSchema = createInsertSchema(evergreenCatalogs).omit({ id: true, createdAt: true });
export type InsertEvergreenCatalog = z.infer<typeof insertEvergreenCatalogSchema>;
export type EvergreenCatalog = typeof evergreenCatalogs.$inferSelect;

export const insertEvergreenCatalogPostSchema = createInsertSchema(evergreenCatalogPosts).omit({ id: true, createdAt: true });
export type InsertEvergreenCatalogPost = z.infer<typeof insertEvergreenCatalogPostSchema>;
export type EvergreenCatalogPost = typeof evergreenCatalogPosts.$inferSelect;

// Pillar Posts table - AI-generated posts based on content pillars from marketing strategy
export const pillarPosts = pgTable("pillar_posts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: varchar("company_id").notNull().references(() => companies.id, { onDelete: "cascade" }),
  pillarName: text("pillar_name").notNull(), // Name of the content pillar
  pillarType: varchar("pillar_type", { length: 50 }).notNull(), // context_setting, capability_building, solution_supporting
  title: text("title").notNull(), // Post title/hook
  platform: varchar("platform", { length: 50 }).notNull().default("linkedin"),
  postContent: text("post_content").notNull(), // Full post content
  hashtags: jsonb("hashtags").default([]).notNull(), // Array of hashtags
  imageUrl: text("image_url"), // Optional image
  callToAction: text("call_to_action"), // Optional CTA
  status: varchar("status", { length: 20 }).notNull().default("pending"), // pending, approved, rejected, posted, scheduled
  reviewNotes: text("review_notes"), // Optional notes from reviewer
  approvedAt: timestamp("approved_at"),
  approvedBy: varchar("approved_by"),
  scheduledFor: timestamp("scheduled_for"), // When to post (if scheduled)
  postedAt: timestamp("posted_at"),
  linkedinPostUrn: text("linkedin_post_urn"), // URN of posted LinkedIn content
  batchId: varchar("batch_id").references(() => weeklyBatches.id, { onDelete: "set null" }),
  // DFY v1 Phase 1 additions (item 10). publishStatus is nullable (no default) so the
  // ADD COLUMN does NOT label legacy non-DFY posts as 'pending'. version/userEdited carry
  // existing-row-safe defaults.
  latePostId: text("late_post_id"),
  publishStatus: varchar("publish_status", { length: 20 }), // pending | published | failed (DFY posts only)
  publishError: text("publish_error"),
  publishAttemptAt: timestamp("publish_attempt_at"),
  publishIdempotencyKey: text("publish_idempotency_key"),
  version: integer("version").notNull().default(1),
  userEdited: boolean("user_edited").notNull().default(false),
  editedAt: timestamp("edited_at"),
  // DFY multi-source: which generation source produced this post. Nullable enum —
  // legacy/self-serve posts have no source; only DFY-cycle posts set it.
  source: dfySourceEnum("source"),
  // DFY Phase 1c (hardening): correlation for idempotent per-post upsert. `sourcePostKey`
  // is a DURABLE upstream artifact id (NOT an array index). The partial-unique below makes
  // callback retries / duplicate n8n executions upsert instead of duplicating.
  cycleId: varchar("cycle_id"),
  sourcePostKey: text("source_post_key"),
  // DFY Phase 2c (#8): deterministic plan ordering, PERSISTED by the assembly finalizer so the
  // approval view is stable across partial callbacks / retries. Nullable — only set on assembled
  // DFY-cycle posts; legacy/self-serve posts leave it null.
  sortOrder: integer("sort_order"),
  // Cockpit slice 3 (Pillar Variance Engine): the LinkedIn format + angle this post was written
  // in — see shared/pillarFormats.ts. Null for posts generated before the engine or outside it.
  format: varchar("format", { length: 32 }),
  mechanic: varchar("mechanic", { length: 32 }),
  // Late analytics ingestion writes per-post metrics here (mirrors generatedPosts.
  // engagementData, same shared-latePostId dedup invariant — see that column's comment).
  engagementData: jsonb("engagement_data").default({}),
  // Signal-Room import idempotency key (phase 0): globally namespaced
  // `sr:<signal-room-client-id>:<brief-key>`. Nullable — only sr-imported rows carry it. The
  // partial unique below is what makes a re-export an UPDATE instead of a duplicate; it also
  // marks the row for the SIGNAL_ROOM_PUBLISH_ENABLED gate (outbox drainer + manual publish
  // hold any `sr:` row while that switch is off) and the "Signal Room" source badge.
  sourceRef: text("source_ref"),
  // Hotel-Edition B1 (migration 0029): media format of the post — foto | karussell | reel | linkedin.
  // NULL on older rows: display derives it (shared/pillarFormats.ts effectiveMediaFormat). Not the
  // LinkedIn `format` column above. video_url / poster_url are reserved for B2 (reels).
  mediaFormat: text("media_format"),
  videoUrl: text("video_url"),
  posterUrl: text("poster_url"),
  // Carousel (migration 0032): the slides as object paths in order, 2..10, slide 1 = image_url (DB CHECKs).
  // NULL = not a carousel. Written only by the Signal-Room import; never client-writable.
  imageUrls: jsonb("image_urls").$type<string[] | null>(),
  // Slide overlays (migration 0034): per picture the Signal-Room slide without its photo (transparent PNG), same
  // order as the pictures; the picture swap lays the owner's photo under it. NULL = none. Written by the import only.
  imageOverlayUrls: jsonb("image_overlay_urls").$type<string[] | null>(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (table) => [
  index("idx_pillar_posts_company").on(table.companyId),
  index("idx_pillar_posts_pillar_type").on(table.pillarType),
  index("idx_pillar_posts_status").on(table.status),
  index("idx_pillar_posts_company_pillar_created").on(table.companyId, table.pillarName, table.createdAt),
  index("idx_pillar_posts_created").on(table.createdAt),
  uniqueIndex("uq_pillar_posts_cycle_source_key")
    .on(table.cycleId, table.source, table.sourcePostKey)
    .where(sql`cycle_id IS NOT NULL`),
  uniqueIndex("uq_pillar_posts_company_source_ref")
    .on(table.companyId, table.sourceRef)
    .where(sql`source_ref IS NOT NULL`),
]);

export const insertPillarPostSchema = createInsertSchema(pillarPosts).omit({ id: true, createdAt: true, approvedAt: true, approvedBy: true, postedAt: true, linkedinPostUrn: true, imageUrls: true, imageOverlayUrls: true });
export type InsertPillarPost = z.infer<typeof insertPillarPostSchema>;
export type PillarPost = typeof pillarPosts.$inferSelect;

// Slice 2 (tenancy hardening): user-facing edit whitelist for PUT /pillar-posts/:id.
// Only copy fields are client-writable — lifecycle/publishing fields (status, publishStatus,
// scheduledFor, linkedinPostUrn, sortOrder, batchId, cycleId, ...) are owned by the
// approval/publish state machines and must never be settable from the browser. Unknown
// keys are stripped by zod's default object behavior, which IS the whitelist.
export const updatePillarPostContentSchema = z.object({
  title: z.string().min(1).optional(),
  postContent: z.string().min(1).optional(),
  imageUrl: z.string().nullable().optional(),
  hashtags: z.array(z.string()).optional(),
  callToAction: z.string().nullable().optional(),
});
export type UpdatePillarPostContent = z.infer<typeof updatePillarPostContentSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// DFY v1 Phase 1 — additive online migration (Step A).
// All tables are NEW; all pillar_posts ADDITIONS are nullable or have
// existing-row-safe defaults (no column mislabels legacy posts on ADD COLUMN).
// The CONCURRENT partial-unique active-cycle index is a SEPARATE non-transactional
// migration (Step C) and is intentionally NOT defined here.
// ═══════════════════════════════════════════════════════════════════════════

// Detailed cycle lifecycle (item 9). Progression: pending → generating → assembling
// → pending_approval → approved → publishing → published. Terminals: empty | failed
// | stalled | superseded. `status` is the coarse bucket used by the (deferred)
// partial-unique active index predicate.
export const dfyCyclePhaseEnum = pgEnum("dfy_cycle_phase", [
  "pending", "generating", "assembling", "pending_approval", "approved",
  "publishing", "published", "empty", "failed", "stalled", "superseded",
]);
export const dfyCycleStatusEnum = pgEnum("dfy_cycle_status", ["active", "terminal"]);
// Slice-1: `reconcile` = ambiguous Zernio outcome (post MAY exist — never auto-redrained, operator
// resolves via dfyOps outbox-resolve); `superseded` = cycle was reset, row must not publish.
export const dfyOutboxStatusEnum = pgEnum("dfy_outbox_status", ["queued", "leased", "published", "failed", "reconcile", "superseded"]);

// dfy_cycles — one orchestration cycle per (company, weekIso, revision).
export const dfyCycles = pgTable("dfy_cycles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: varchar("company_id").notNull().references(() => companies.id, { onDelete: "cascade" }),
  weekIso: text("week_iso").notNull(),
  revision: integer("revision").notNull().default(1),
  phase: dfyCyclePhaseEnum("phase").notNull().default("pending"),
  status: dfyCycleStatusEnum("status").notNull().default("active"),
  supersededAt: timestamp("superseded_at"),
  startedAt: timestamp("started_at").defaultNow().notNull(),
  lastTransitionAt: timestamp("last_transition_at").defaultNow().notNull(),
  completedAt: timestamp("completed_at"),
  n8nExecutionId: text("n8n_execution_id"),
  contractVersion: text("contract_version").notNull().default("1"),
  errorCode: varchar("error_code", { length: 80 }),
  errorMessage: text("error_message"),
  errorDetails: jsonb("error_details"),
  // DFY Phase 1c (hardening): crash-safe approval email. `queuedAt` is stamped INSIDE the
  // finalizer tx; `notifiedAt` is stamped ONLY after a successful SMTP send (post-commit
  // drain). A crash between commit and send leaves it re-drainable, never falsely notified.
  approvalNotificationQueuedAt: timestamp("approval_notification_queued_at"),
  approvalNotifiedAt: timestamp("approval_notified_at"),
  // Slice 7 (B′ autonomous repurposing): ONE-TIME selection guard (rr2-H2). The stage-1 discovery
  // callback CASes this null→now so a duplicate/late discovery callback can't run video selection (and
  // reserve DIFFERENT videos) a second time for the cycle. Null = selection not yet finalized.
  selectionFinalizedAt: timestamp("selection_finalized_at"),
  // Cockpit slice 3: the slot plan (shared/pillarFormats.ts SlotPlan) handed to n8n for this cycle.
  // Stored at first build so outbox re-sends carry the byte-identical payload (signer contract).
  slotPlan: jsonb("slot_plan"),
  // DFY for hotels (migration 0028): the content source's channel + brand snapshot (dfyContentContext),
  // stored at first build for the same reason as slotPlan (byte-identical re-sends).
  contentContext: jsonb("content_context"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  index("idx_dfy_cycles_company_week").on(t.companyId, t.weekIso),
  index("idx_dfy_cycles_status").on(t.status),
  index("idx_dfy_cycles_phase").on(t.phase),
]);

export const insertDfyCycleSchema = createInsertSchema(dfyCycles).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertDfyCycle = z.infer<typeof insertDfyCycleSchema>;
export type DfyCycle = typeof dfyCycles.$inferSelect;

// dfy_publish_outbox — durable per-post publish queue (item 18). Drained in-process
// on enqueue + on startup + via manual resume; leased with FOR UPDATE SKIP LOCKED.
export const dfyPublishOutbox = pgTable("dfy_publish_outbox", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  cycleId: varchar("cycle_id").references(() => dfyCycles.id, { onDelete: "cascade" }),
  companyId: varchar("company_id").notNull().references(() => companies.id, { onDelete: "cascade" }),
  batchId: varchar("batch_id").references(() => weeklyBatches.id, { onDelete: "set null" }),
  pillarPostId: varchar("pillar_post_id").notNull().references(() => pillarPosts.id, { onDelete: "cascade" }),
  status: dfyOutboxStatusEnum("status").notNull().default("queued"),
  attempts: integer("attempts").notNull().default(0),
  // Durable per-post key. At-most-once: written before the Late.dev call; reconcile
  // by querying Late.dev (no caller idempotency key support — Zernio go/no-go 2026-06-04).
  idempotencyKey: text("idempotency_key").notNull(),
  leaseOwner: varchar("lease_owner"),
  leasedAt: timestamp("leased_at"),
  latePostId: text("late_post_id"),
  lastError: text("last_error"),
  providerRequest: jsonb("provider_request"),
  providerResponse: jsonb("provider_response"),
  publishedAt: timestamp("published_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("uq_dfy_outbox_idempotency_key").on(t.idempotencyKey),
  index("idx_dfy_outbox_status").on(t.status),
  index("idx_dfy_outbox_company").on(t.companyId),
  index("idx_dfy_outbox_cycle").on(t.cycleId),
]);

export const insertDfyPublishOutboxSchema = createInsertSchema(dfyPublishOutbox).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertDfyPublishOutbox = z.infer<typeof insertDfyPublishOutboxSchema>;
export type DfyPublishOutbox = typeof dfyPublishOutbox.$inferSelect;

// dfy_events — append-only audit (item 22). cycle/batch/post IDs are bare varchars
// (no FK) so audit rows survive deletion of the entities they describe.
export const dfyEvents = pgTable("dfy_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  actorType: varchar("actor_type", { length: 20 }).notNull(), // admin | system | agent | user
  actorId: varchar("actor_id"),
  companyId: varchar("company_id").references(() => companies.id, { onDelete: "cascade" }),
  cycleId: varchar("cycle_id"),
  batchId: varchar("batch_id"),
  pillarPostId: varchar("pillar_post_id"),
  action: text("action").notNull(),
  oldState: varchar("old_state", { length: 40 }),
  newState: varchar("new_state", { length: 40 }),
  correlation: jsonb("correlation"),
  detail: jsonb("detail"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => [
  index("idx_dfy_events_company").on(t.companyId),
  index("idx_dfy_events_cycle").on(t.cycleId),
  index("idx_dfy_events_created").on(t.createdAt),
]);

export const insertDfyEventSchema = createInsertSchema(dfyEvents).omit({ id: true, createdAt: true });
export type InsertDfyEvent = z.infer<typeof insertDfyEventSchema>;
export type DfyEvent = typeof dfyEvents.$inferSelect;

// ───────────────────────────────────────────────────────────────────────────
// DFY Phase 1b — generation-dispatch layer (multi-source fan-out, item 13 generalized).
// One row per (cycle, source) for the 3 POST-producing sources. `visuals` (brand
// images) is deliberately NOT a source here — it produces media, not posts.
// dfy_cycles.n8n_execution_id is now vestigial (kept, additive-safe); this table is
// the authoritative per-source dispatch record.
// ───────────────────────────────────────────────────────────────────────────
// dfySourceEnum is defined near the top (above pillar_posts).
export const dfyDispatchStatusEnum = pgEnum("dfy_dispatch_status", [
  "pending", "dispatched", "completed", "failed", "timed_out",
]);

export const dfyGenerationDispatch = pgTable("dfy_generation_dispatch", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  cycleId: varchar("cycle_id").notNull().references(() => dfyCycles.id, { onDelete: "cascade" }),
  companyId: varchar("company_id").notNull().references(() => companies.id, { onDelete: "cascade" }),
  source: dfySourceEnum("source").notNull(),
  status: dfyDispatchStatusEnum("status").notNull().default("pending"),
  attempts: integer("attempts").notNull().default(0),
  // DFY Phase 1c (hardening): v1 default contract = ONE aggregate callback per (cycle,source),
  // so this stays null. If a source must stream, it declares expected_count and the dispatch
  // completes only when all items are terminal.
  expectedCount: integer("expected_count"),
  // Per-(cycle,source) idempotency for the OUTBOUND dispatch (transition cycle→generating
  // only after a confirmed send; resend re-uses the key).
  idempotencyKey: text("idempotency_key").notNull(),
  n8nExecutionId: text("n8n_execution_id"),
  webhookRef: varchar("webhook_ref", { length: 80 }), // which workflow/env URL was hit (audit)
  dispatchedAt: timestamp("dispatched_at"),
  completedAt: timestamp("completed_at"),
  deadlineAt: timestamp("deadline_at"), // per-source timeout → partial-generation policy
  leaseOwner: varchar("lease_owner"),
  leasedAt: timestamp("leased_at"),
  postsReceived: integer("posts_received").notNull().default(0),
  lastError: text("last_error"),
  errorDetails: jsonb("error_details"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("uq_dfy_dispatch_cycle_source").on(t.cycleId, t.source),
  uniqueIndex("uq_dfy_dispatch_idempotency_key").on(t.idempotencyKey),
  index("idx_dfy_dispatch_status").on(t.status),
  index("idx_dfy_dispatch_cycle").on(t.cycleId),
]);

export const insertDfyGenerationDispatchSchema = createInsertSchema(dfyGenerationDispatch).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertDfyGenerationDispatch = z.infer<typeof insertDfyGenerationDispatchSchema>;
export type DfyGenerationDispatch = typeof dfyGenerationDispatch.$inferSelect;

// ───────────────────────────────────────────────────────────────────────────
// DFY Slice 7 — autonomous weekly engine (option B′). Three additive tables:
//  1. dfy_repurpose_sources  — per-company YouTube source channels (config UI).
//  2. dfy_repurposed_videos  — RESERVATION ledger (reserve-before-pay; the
//     load-bearing invariant that prevents repurposing the same video twice).
//  3. dfy_weekly_schedule    — producer-state arbiter (app vs legacy) per
//     (company, week), with attempt_epoch fencing for reset/revision.
// See DFY_SLICE7_AUTONOMOUS_PLAN.md (R1–R10).
// ───────────────────────────────────────────────────────────────────────────

// 1. Source channels (per company). Resolution runs via an n8n webhook (Supadata),
//    so the app never holds the Supadata key; resolution_status tracks that round-trip.
export const dfyRepurposeResolutionStatusEnum = pgEnum("dfy_repurpose_resolution_status", [
  "pending", "resolved", "failed",
]);

export const dfyRepurposeSources = pgTable("dfy_repurpose_sources", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: varchar("company_id").notNull().references(() => companies.id, { onDelete: "cascade" }),
  provider: varchar("provider", { length: 20 }).notNull().default("youtube"),
  channelInput: text("channel_input").notNull(), // raw user entry (@handle / /channel/UC… / URL)
  channelId: text("channel_id"), // canonical, null until resolved
  channelTitle: text("channel_title"),
  resolutionStatus: dfyRepurposeResolutionStatusEnum("resolution_status").notNull().default("pending"),
  resolutionError: text("resolution_error"),
  resolveAttempts: integer("resolve_attempts").notNull().default(0),
  resolveLeaseUntil: timestamp("resolve_lease_until"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  index("idx_dfy_repurpose_sources_company").on(t.companyId),
  // Canonical dedup: a handle and its /channel/UC… form resolve to the same id → reject dup.
  uniqueIndex("uq_dfy_repurpose_sources_company_channel")
    .on(t.companyId, t.channelId)
    .where(sql`channel_id IS NOT NULL`),
]);

export const insertDfyRepurposeSourceSchema = createInsertSchema(dfyRepurposeSources).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertDfyRepurposeSource = z.infer<typeof insertDfyRepurposeSourceSchema>;
export type DfyRepurposeSource = typeof dfyRepurposeSources.$inferSelect;

// 2. Reservation ledger. status lifecycle: reserved → completed (post ingested) |
//    released (terminal per-video failure) | quarantined (dispatch timed out, n8n may
//    still be running — NEVER auto-released into double-pay). Rows are immutable attempts;
//    a re-selection after release/quarantine-resolution is a NEW row.
export const dfyRepurposeVideoStatusEnum = pgEnum("dfy_repurpose_video_status", [
  "reserved", "completed", "quarantined", "released",
]);

export const dfyRepurposedVideos = pgTable("dfy_repurposed_videos", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: varchar("company_id").notNull().references(() => companies.id, { onDelete: "cascade" }),
  videoId: text("video_id").notNull(), // YouTube video id (reuse repurposeUrl.ts extraction)
  channelId: text("channel_id"),
  cycleId: varchar("cycle_id").notNull(), // reserving cycle; plain varchar (audit survives cycle delete)
  attemptEpoch: integer("attempt_epoch").notNull().default(1), // fences superseded cycles (R4)
  repurposeUrl: text("repurpose_url").notNull(), // canonical URL handed to n8n
  sourcePostKey: text("source_post_key").notNull(), // = ${cycleId}:repurposing:${sha1(url)} (correlation, H-4)
  status: dfyRepurposeVideoStatusEnum("status").notNull().default("reserved"),
  failReason: text("fail_reason"),
  reservedAt: timestamp("reserved_at").defaultNow().notNull(),
  completedAt: timestamp("completed_at"),
  quarantinedAt: timestamp("quarantined_at"),
  releasedAt: timestamp("released_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => [
  index("idx_dfy_repurposed_videos_company").on(t.companyId),
  index("idx_dfy_repurposed_videos_status").on(t.status),
  index("idx_dfy_repurposed_videos_cycle").on(t.cycleId),
  // At most ONE live reservation per (company, video) — across weeks/cycles/reset.
  // quarantined IS a live state (re-review-3-H2): a timed-out-but-maybe-running video
  // must not be re-selectable until its dispatch is provably terminal.
  uniqueIndex("uq_dfy_repurposed_videos_live")
    .on(t.companyId, t.videoId)
    .where(sql`status IN ('reserved','completed','quarantined')`),
]);

export const insertDfyRepurposedVideoSchema = createInsertSchema(dfyRepurposedVideos).omit({ id: true, createdAt: true });
export type InsertDfyRepurposedVideo = z.infer<typeof insertDfyRepurposedVideoSchema>;
export type DfyRepurposedVideo = typeof dfyRepurposedVideos.$inferSelect;

// 3. Producer-state arbiter. UNIQUE(company, week); the single source of truth for who
//    produces a company-week (app vs legacy). attempt_epoch + current_cycle_id fence
//    stale callbacks from a superseded cycle after reset/revision (re-review-2-H5).
export const dfyScheduleProducerEnum = pgEnum("dfy_schedule_producer", ["app", "legacy"]);
export const dfyScheduleStatusEnum = pgEnum("dfy_schedule_status", [
  "finalized", "generating", "done", "aborted",
]);

export const dfyWeeklySchedule = pgTable("dfy_weekly_schedule", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: varchar("company_id").notNull().references(() => companies.id, { onDelete: "cascade" }),
  weekIso: text("week_iso").notNull(),
  producer: dfyScheduleProducerEnum("producer").notNull().default("app"),
  status: dfyScheduleStatusEnum("status").notNull().default("finalized"),
  attemptEpoch: integer("attempt_epoch").notNull().default(1),
  currentCycleId: varchar("current_cycle_id"),
  eligibilitySnapshot: jsonb("eligibility_snapshot"), // inputs to the eligibility decision (R4)
  heartbeatAt: timestamp("heartbeat_at"), // app liveness for stalled-finalized takeover (R4/3-H3)
  finalizedAt: timestamp("finalized_at").defaultNow().notNull(),
  generatingAt: timestamp("generating_at"),
  doneAt: timestamp("done_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  // The producer-state arbiter: exactly one row per (company, week). The app claims via
  // INSERT … ON CONFLICT DO NOTHING; the winner creates the cycle in the same tx.
  uniqueIndex("uq_dfy_weekly_schedule_company_week").on(t.companyId, t.weekIso),
  index("idx_dfy_weekly_schedule_status").on(t.status),
  index("idx_dfy_weekly_schedule_producer").on(t.producer),
]);

export const insertDfyWeeklyScheduleSchema = createInsertSchema(dfyWeeklySchedule).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertDfyWeeklySchedule = z.infer<typeof insertDfyWeeklyScheduleSchema>;
export type DfyWeeklySchedule = typeof dfyWeeklySchedule.$inferSelect;

// ───────────────────────────────────────────────────────────────────────────
// DFY R9 — transport security (DFY_R9_TRANSPORT_PLAN.md). Two additive tables:
//  1. dfy_dispatch_claims — the dispatch-auth ledger (D2). One row per claimKey,
//     UPSERTed at SEND time (`issued`), CAS→`claimed` by the n8n claim-echo call
//     (fencing token issued), `quarantined` on expiry (never auto-reissued, rr3-H1).
//  2. dfy_dispatch_nonces — Postgres-backed anti-replay (rr3-M3). Burned inside the
//     claim transaction; unique(scope, nonce) makes concurrent replay lose at the DB.
// ───────────────────────────────────────────────────────────────────────────
export const dfyClaimStatusEnum = pgEnum("dfy_claim_status", ["issued", "claimed", "quarantined"]);

export const dfyDispatchClaims = pgTable("dfy_dispatch_claims", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  // generation → the dispatch idempotency key (`${cycleId}:${source}`); discovery →
  // `${cycleId}:discovery`; resolve → `resolve:${sourceId}:${resolveAttempt}` (attempt is
  // bumped at lease time, so every legitimate resolver retry is a FRESH key — rd2-H2).
  claimKey: text("claim_key").notNull(),
  kind: varchar("kind", { length: 20 }).notNull(), // generation | discovery | resolve (per-path telemetry, rd1-M5)
  companyId: varchar("company_id").notNull().references(() => companies.id, { onDelete: "cascade" }),
  cycleId: varchar("cycle_id"),     // bare varchar (audit survives cycle delete); null for resolve
  dispatchId: varchar("dispatch_id"), // null for discovery/resolve
  attemptEpoch: integer("attempt_epoch").notNull().default(1),
  status: dfyClaimStatusEnum("status").notNull().default("issued"),
  payloadHash: text("payload_hash").notNull(), // JCS-canonical sha256 of the body minus `auth` (rd2-M2)
  fencingTokenHash: text("fencing_token_hash"), // sha256(token); token itself is DERIVED, never stored (rd1-H2)
  claimNonce: text("claim_nonce"), // the nonce that won the claim — re-ack derivation input
  n8nExecutionId: text("n8n_execution_id"), // rr3-H1 stopped-proof for the quarantine runbook
  signedAt: timestamp("signed_at").defaultNow().notNull(),
  claimedAt: timestamp("claimed_at"),
  quarantinedAt: timestamp("quarantined_at"),
  expiresAt: timestamp("expires_at").notNull(), // dispatch deadline + 30 min slack, DB clock
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("uq_dfy_dispatch_claims_key").on(t.claimKey),
  index("idx_dfy_dispatch_claims_status_expires").on(t.status, t.expiresAt), // quarantine-sweep predicate
  index("idx_dfy_dispatch_claims_cycle").on(t.cycleId), // callback correlation
]);

export const insertDfyDispatchClaimSchema = createInsertSchema(dfyDispatchClaims).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertDfyDispatchClaim = z.infer<typeof insertDfyDispatchClaimSchema>;
export type DfyDispatchClaim = typeof dfyDispatchClaims.$inferSelect;

export const dfyDispatchNonces = pgTable("dfy_dispatch_nonces", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  scope: varchar("scope", { length: 32 }).notNull(), // v1: 'dispatch-auth'
  nonce: varchar("nonce", { length: 64 }).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("uq_dfy_dispatch_nonces_scope_nonce").on(t.scope, t.nonce),
  index("idx_dfy_dispatch_nonces_created").on(t.createdAt), // retention-sweep predicate (7 d, batched)
]);

export type DfyDispatchNonce = typeof dfyDispatchNonces.$inferSelect;

// ───────────────────────────────────────────────────────────────────────────
// Post-Paket lead-magnet intake (slice 4b, 2026-07-26 design). The A-Team app is the token
// CUSTODIAN for the hotel Post-Paket: the Mac worker HMAC-pushes render state + artifact keys to
// /internal/pp/ingest; the app persists tokens mint-once-immutable and mints short presigns at READ
// time for the public poll (/pp/:poll_token/status) + pack download (/pack/:pack_token). Mirrors the
// preview_pages pattern; pp_ingest_nonces mirrors dfy_dispatch_nonces (durable anti-replay).
// ───────────────────────────────────────────────────────────────────────────
export const ppTokens = pgTable("pp_tokens", {
  id: serial("id").primaryKey(),
  submissionId: varchar("submission_id", { length: 64 }).unique().notNull(), // upsert key; NEVER leaked publicly
  pollToken: varchar("poll_token", { length: 64 }).unique().notNull(), // public status key (page-minted)
  packToken: varchar("pack_token", { length: 64 }).unique(), // public pack key (worker-minted); null until pack_ready
  status: varchar("status", { length: 32 }).notNull(),
  livePreview: boolean("live_preview").default(true).notNull(), // false ⇒ page stops polling (item 16)
  previewKeys: jsonb("preview_keys").default([]).notNull(), // validated S3 keys
  packKey: text("pack_key"), // validated S3 key
  businessType: varchar("business_type", { length: 64 }), // observability only
  domain: varchar("domain", { length: 255 }), // observability only
  expiresAt: timestamp("expires_at").notNull(), // now()+14d; re-anchored when the pack is first recorded
  revokedAt: timestamp("revoked_at"), // revocation (abuse / DSGVO)
  lastError: text("last_error"), // redacted
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  index("idx_pp_tokens_status").on(t.status),
  index("idx_pp_tokens_expires").on(t.expiresAt), // sweep predicate
]);
export type PpToken = typeof ppTokens.$inferSelect;
export type InsertPpToken = typeof ppTokens.$inferInsert;

export const ppIngestNonces = pgTable("pp_ingest_nonces", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  nonce: varchar("nonce", { length: 64 }).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("uq_pp_ingest_nonces_nonce").on(t.nonce), // concurrent replay loses at the DB
  index("idx_pp_ingest_nonces_created").on(t.createdAt), // retention-sweep predicate
]);
export type PpIngestNonce = typeof ppIngestNonces.$inferSelect;

// DSGVO/retention tombstone (Hotel-Check U11). Written by eraseSubmission and the expiry sweep in the
// same advisory-locked tx that deletes the pp_tokens row; ingest checks it under the same lock and
// answers 410, so a delayed or retried push can never revive an erased pack. No personal data.
export const ppErased = pgTable("pp_erased", {
  submissionId: text("submission_id").primaryKey(),
  erasedAt: timestamp("erased_at", { withTimezone: true }).defaultNow().notNull(),
});
export type PpErased = typeof ppErased.$inferSelect;

// ───────────────────────────────────────────────────────────────────────────
// Signal-Room client-lane import (PLAN.md rev. 4, phases 0+1). All tables additive.
// Posts land in pillar_posts (source_ref-keyed, see there); these tables carry the
// import machinery (audit, anti-replay, ledger) and the phase-1 second pillar system
// + weekly market snapshot. The dfy_source enum is deliberately NOT touched — sr rows
// in a DFY cycle have source NULL and are identified by source_ref 'sr:%'.
// ───────────────────────────────────────────────────────────────────────────

// Admin mapping audit (PLAN #1): one row per company↔client mapping change.
export const signalRoomMappingEvents = pgTable("signal_room_mapping_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: varchar("company_id").notNull(), // bare varchar — audit survives company delete
  oldClientId: text("old_client_id"),
  newClientId: text("new_client_id"),
  oldSlug: text("old_slug"),
  newSlug: text("new_slug"),
  adminId: varchar("admin_id"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => [
  index("idx_sr_mapping_events_company").on(t.companyId),
]);
export type SignalRoomMappingEvent = typeof signalRoomMappingEvents.$inferSelect;

// Single-use HMAC nonces (PLAN #4). Burned in the same transaction as the import's writes so a
// concurrent replay loses at the DB (mirrors pp_ingest_nonces / dfy_dispatch_nonces). Pruning is
// opportunistic: every ACCEPTED import deletes rows older than 48 h.
export const signalRoomImportNonces = pgTable("signal_room_import_nonces", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  nonce: varchar("nonce", { length: 64 }).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("uq_sr_import_nonces_nonce").on(t.nonce),
  index("idx_sr_import_nonces_created").on(t.createdAt),
]);
export type SignalRoomImportNonce = typeof signalRoomImportNonces.$inferSelect;

// Durable import ledger (PLAN #7a). One row per import attempt that passed auth+schema; a retried
// importId returns the recorded result ONLY when payload_sha256 matches, else a non-retryable
// conflict (an exporter bug must surface, not hide). Retained ≥90 days (no auto-prune in-app).
export const signalRoomImportRuns = pgTable("signal_room_import_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  importId: text("import_id").notNull(), // exporter-supplied UUID
  payloadSha256: varchar("payload_sha256", { length: 64 }).notNull(),
  schemaVersion: integer("schema_version").notNull(),
  keyId: varchar("key_id", { length: 64 }),
  companyId: varchar("company_id"), // bare varchar — ledger survives company delete; null for market-only
  clientId: text("client_id"),
  dryRun: boolean("dry_run").notNull().default(false),
  status: varchar("status", { length: 20 }).notNull(), // accepted | rejected | error
  errorClass: varchar("error_class", { length: 80 }),
  decisions: jsonb("decisions"), // per-item {key, status, reason?, version?} + pillar/market outcomes
  counts: jsonb("counts"), // {created, updated, skipped, conflicts, rejected, imageFailed, ...}
  createdAt: timestamp("created_at").defaultNow().notNull(),
  completedAt: timestamp("completed_at"),
  // Atomic importId claim (Hotel-Edition B-1, migration 0029): status 'processing' + token while an
  // import runs; only the claim holder may finish the row, an accepted row is never overwritten.
  claimedAt: timestamp("claimed_at"),
  claimToken: text("claim_token"),
}, (t) => [
  uniqueIndex("uq_sr_import_runs_import_id").on(t.importId),
  index("idx_sr_import_runs_created").on(t.createdAt),
  index("idx_sr_import_runs_company").on(t.companyId),
]);
export type SignalRoomImportRun = typeof signalRoomImportRuns.$inferSelect;

// A Signal-Room post the customer deleted (Hotel-Edition B-3, migration 0029). The import never
// re-creates a post whose stable source_ref has a tombstone (decision `deleted_by_customer`); only an
// admin action removes it. pillar_post_id lets a delete retry after a lost response answer success.
export const srPostTombstones = pgTable("sr_post_tombstones", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: varchar("company_id").notNull().references(() => companies.id, { onDelete: "cascade" }),
  sourceRef: text("source_ref").notNull(),
  pillarPostId: varchar("pillar_post_id"),
  deletedAt: timestamp("deleted_at").defaultNow().notNull(),
  actor: varchar("actor"),
}, (t) => [
  uniqueIndex("uq_sr_post_tombstones_company_ref").on(t.companyId, t.sourceRef),
  index("idx_sr_post_tombstones_post").on(t.companyId, t.pillarPostId),
]);
export type SrPostTombstone = typeof srPostTombstones.$inferSelect;

// ───────────────────────────────────────────────────────────────────────────
// Brand kit (Block 2c, migration 0025). Customer-maintained in the app (Profil "Marke" card);
// Signal Room pulls it read-only over the signed channel. `companies.brandVersion` above is the
// single mutation/ordering token — NOT a per-row revision here.
// ───────────────────────────────────────────────────────────────────────────

export const companyBrand = pgTable("company_brand", {
  id: serial("id").primaryKey(),
  companyId: varchar("company_id").notNull().references(() => companies.id, { onDelete: "cascade" }),
  logoObjectKey: text("logo_object_key"),
  logoPlacement: text("logo_placement").notNull().default("none"), // all | first | none
  colors: jsonb("colors").notNull().default({}), // { primary, secondary, text }
  fonts: jsonb("fonts").notNull().default({}), // { heading, body }
  styleNote: text("style_note").notNull().default(""),
  referenceUploadIds: jsonb("reference_upload_ids").notNull().default([]), // content_uploads ids, ≤6
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  updatedBy: text("updated_by"), // user id
}, (t) => [
  uniqueIndex("uq_company_brand_company").on(t.companyId),
]);
export type CompanyBrand = typeof companyBrand.$inferSelect;
export type InsertCompanyBrand = typeof companyBrand.$inferInsert;

// Logo candidates extracted from "Aus Datei übernehmen" — server-recorded, company-owned, must be
// adopted within 24h; a claim lease (claimedAt) prevents a double concurrent adopt.
export const brandLogoCandidates = pgTable("brand_logo_candidates", {
  id: serial("id").primaryKey(),
  companyId: varchar("company_id").notNull().references(() => companies.id, { onDelete: "cascade" }),
  objectKey: text("object_key").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  claimedAt: timestamp("claimed_at"),
}, (t) => [
  index("idx_brand_logo_candidates_company").on(t.companyId),
]);
export type BrandLogoCandidate = typeof brandLogoCandidates.$inferSelect;

// Upload-intent ledger (Round 2/3): written BEFORE the S3 write as `pending`, flipped to `live` in
// the same tx as the pointer update, to `retired` when replaced. Daily cleanup deletes stale rows.
export const brandAssets = pgTable("brand_assets", {
  id: serial("id").primaryKey(),
  companyId: varchar("company_id").notNull().references(() => companies.id, { onDelete: "cascade" }),
  objectKey: text("object_key").notNull(),
  kind: text("kind").notNull(), // logo | candidate
  state: text("state").notNull().default("pending"), // pending | live | retired
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  index("idx_brand_assets_company").on(t.companyId),
  index("idx_brand_assets_state_created").on(t.state, t.createdAt),
]);
export type BrandAsset = typeof brandAssets.$inferSelect;

// Phase 1 (#8): the SECOND pillar system — Instagram "Evidenz-Säulen" per company, imported from
// Signal Room. Deliberately separate from marketingStrategies.contentPillars (LinkedIn/strategy).
// Merge-upsert by (companyId, normalizedName); rows missing from a full import are soft-retired
// (retiredAt), never hard-deleted, so prefs and history survive re-imports by construction.
export const instagramPillars = pgTable("instagram_pillars", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: varchar("company_id").notNull().references(() => companies.id, { onDelete: "cascade" }),
  rank: integer("rank").notNull(),
  name: text("name").notNull(),
  normalizedName: text("normalized_name").notNull(), // persisted via shared/signalRoom.ts normalizePillarName
  claim: text("claim"),
  status: varchar("status", { length: 30 }), // never-posted | dormant | underused | workhorse (enum-text)
  factsJson: jsonb("facts_json").default([]).notNull(), // [{text, source?}]
  series: text("series"),
  cadence: text("cadence"), // "Takt"
  seedIdeasJson: jsonb("seed_ideas_json").default([]).notNull(),
  keywordsJson: jsonb("keywords_json").default([]).notNull(),
  coverageJson: jsonb("coverage_json").default({}).notNull(),
  source: varchar("source", { length: 30 }).notNull().default("signal-room"),
  importedAt: timestamp("imported_at").defaultNow().notNull(),
  retiredAt: timestamp("retired_at"), // soft-retire when missing from an import
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("uq_instagram_pillars_company_norm").on(t.companyId, t.normalizedName),
  index("idx_instagram_pillars_company").on(t.companyId),
]);
export type InstagramPillar = typeof instagramPillars.$inferSelect;

// Phase 1 (#9): pillar-local UI state in its OWN table, joined at read time on normalizedName —
// survives re-imports by construction (the import never touches this table).
export const instagramPillarPrefs = pgTable("instagram_pillar_prefs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: varchar("company_id").notNull().references(() => companies.id, { onDelete: "cascade" }),
  normalizedName: text("normalized_name").notNull(),
  hidden: boolean("hidden").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("uq_instagram_pillar_prefs_company_norm").on(t.companyId, t.normalizedName),
]);
export type InstagramPillarPref = typeof instagramPillarPrefs.$inferSelect;

// Phase 1 (#10): weekly market snapshot, split for tenancy. market_snapshots.cardJson is
// GLOBAL-ONLY content (no competitor material); per-company highlights live in
// company_market_highlights. Card + ALL highlights of a week import in ONE transaction and
// complete=true is set only then — reads serve solely complete, version-matched sets.
export const marketSnapshots = pgTable("market_snapshots", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  weekStart: date("week_start").notNull(), // ISO-Monday, Europe/Berlin (validated at import)
  cardJson: jsonb("card_json").notNull(),
  sourceVersion: integer("source_version").notNull().default(1), // higher wins on re-import
  complete: boolean("complete").notNull().default(false),
  importedAt: timestamp("imported_at").defaultNow().notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("uq_market_snapshots_week").on(t.weekStart),
]);
export type MarketSnapshot = typeof marketSnapshots.$inferSelect;

export const companyMarketHighlights = pgTable("company_market_highlights", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: varchar("company_id").notNull().references(() => companies.id, { onDelete: "cascade" }),
  weekStart: date("week_start").notNull(),
  highlightsJson: jsonb("highlights_json").notNull(),
  sourceVersion: integer("source_version").notNull().default(1),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("uq_company_market_highlights_company_week").on(t.companyId, t.weekStart),
]);
export type CompanyMarketHighlight = typeof companyMarketHighlights.$inferSelect;

// Preview Pages: Zod schemas and types
export const postContentSchema = z.object({
  text: z.string(),
  hashtags: z.array(z.string()).optional(),
});
export type PostContent = z.infer<typeof postContentSchema>;

export const insertPreviewPageSchema = createInsertSchema(previewPages, {
  slug: z.string().min(3).max(255),
  companyName: z.string().min(1).max(255),
  companyCity: z.string().max(255).optional().nullable(),
  companyWebsite: z.string().url().or(z.literal("")).optional().nullable(),
  contactEmail: z.string().email().or(z.literal("")).optional().nullable(),
  segment: z.string().max(100).optional().nullable(),
  photoUrls: z.array(z.string()).optional(),
  postInstagram: postContentSchema.optional(),
  postFacebook: postContentSchema.optional(),
  postLinkedin: postContentSchema.optional(),
}).omit({ id: true, createdAt: true, views: true, ctaClicks: true, firstViewedAt: true, lastViewedAt: true, convertedAt: true });

export type InsertPreviewPage = z.infer<typeof insertPreviewPageSchema>;
export type PreviewPage = typeof previewPages.$inferSelect;

// Relations
export const usersRelations = relations(users, ({ one, many }) => ({
  company: one(companies, {
    fields: [users.id],
    references: [companies.userId],
  }),
  automations: many(automations),
  n8nWorkflows: many(n8nWorkflows),
  contentItems: many(contentItems),
  socialMediaGoals: many(socialMediaGoals),
  contentUploads: many(contentUploads),
  socialMediaConnections: many(socialMediaConnections),
  lateSocialAccounts: many(lateSocialAccounts),
}));

export const companiesRelations = relations(companies, ({ one }) => ({
  user: one(users, {
    fields: [companies.userId],
    references: [users.id],
  }),
  lateProfile: one(lateProfiles, {
    fields: [companies.id],
    references: [lateProfiles.companyId],
  }),
}));

export const lateProfilesRelations = relations(lateProfiles, ({ one }) => ({
  company: one(companies, {
    fields: [lateProfiles.companyId],
    references: [companies.id],
  }),
}));

export const automationsRelations = relations(automations, ({ one }) => ({
  user: one(users, {
    fields: [automations.userId],
    references: [users.id],
  }),
}));

export const n8nWorkflowsRelations = relations(n8nWorkflows, ({ one }) => ({
  user: one(users, {
    fields: [n8nWorkflows.userId],
    references: [users.id],
  }),
}));

export const contentItemsRelations = relations(contentItems, ({ one }) => ({
  user: one(users, {
    fields: [contentItems.userId],
    references: [users.id],
  }),
}));

export const socialMediaGoalsRelations = relations(socialMediaGoals, ({ one }) => ({
  user: one(users, {
    fields: [socialMediaGoals.userId],
    references: [users.id],
  }),
}));

export const contentUploadsRelations = relations(contentUploads, ({ one }) => ({
  user: one(users, {
    fields: [contentUploads.userId],
    references: [users.id],
  }),
  reviewer: one(users, {
    fields: [contentUploads.reviewedBy],
    references: [users.id],
  }),
}));

export const visualBrandRequestsRelations = relations(visualBrandRequests, ({ one }) => ({
  user: one(users, {
    fields: [visualBrandRequests.userId],
    references: [users.id],
  }),
  company: one(companies, {
    fields: [visualBrandRequests.companyId],
    references: [companies.id],
  }),
}));

export const industryNewsRelations = relations(industryNews, ({ one }) => ({
  company: one(companies, {
    fields: [industryNews.companyId],
    references: [companies.id],
  }),
  reviewer: one(users, {
    fields: [industryNews.reviewedBy],
    references: [users.id],
  }),
}));

export const repurposingContentRelations = relations(repurposingContent, ({ one }) => ({
  company: one(companies, {
    fields: [repurposingContent.companyId],
    references: [companies.id],
  }),
  reviewer: one(users, {
    fields: [repurposingContent.reviewedBy],
    references: [users.id],
  }),
}));

export const evergreenCatalogsRelations = relations(evergreenCatalogs, ({ one, many }) => ({
  company: one(companies, {
    fields: [evergreenCatalogs.companyId],
    references: [companies.id],
  }),
  posts: many(evergreenCatalogPosts),
}));

export const evergreenCatalogPostsRelations = relations(evergreenCatalogPosts, ({ one }) => ({
  catalog: one(evergreenCatalogs, {
    fields: [evergreenCatalogPosts.catalogId],
    references: [evergreenCatalogs.id],
  }),
}));

export const oauthAppsRelations = relations(oauthApps, ({ many }) => ({
  socialMediaConnections: many(socialMediaConnections),
}));

export const socialMediaConnectionsRelations = relations(socialMediaConnections, ({ one }) => ({
  user: one(users, {
    fields: [socialMediaConnections.userId],
    references: [users.id],
  }),
  oauthApp: one(oauthApps, {
    fields: [socialMediaConnections.assignedOauthAppId],
    references: [oauthApps.id],
  }),
}));

export const lateSocialAccountsRelations = relations(lateSocialAccounts, ({ one }) => ({
  user: one(users, {
    fields: [lateSocialAccounts.userId],
    references: [users.id],
  }),
}));

// Zod schemas
export const insertUserSchema = createInsertSchema(users).pick({
  email: true,
  firstName: true,
  lastName: true,
  profileImageUrl: true,
  role: true,
});

export const upsertUserSchema = createInsertSchema(users).pick({
  id: true,
  email: true,
  firstName: true,
  lastName: true,
  profileImageUrl: true,
  role: true,
});

// Customer-writable company schema (finalize, POST /api/companies, PATCH /api/companies/me via
// updateCompanyProfileSchema). Server/admin-owned fields are omitted so a customer can never set them:
// zod strips unknown keys, so e.g. {"tier":"dfy"} in a profile PATCH is silently dropped (2026-10-02,
// Codex security review: the tier flip is admin/billing-only).
export const insertCompanySchema = createInsertSchema(companies).omit({
  id: true,
  userId: true,
  passwordHash: true,
  passwordUpdatedAt: true,
  createdAt: true,
  updatedAt: true,
  tier: true,
  dfyActivatedAt: true,
  hasStrategyAddon: true,
  strategyGeneratedAt: true,
  clientTelegramChatId: true,
  signalRoomClientId: true,
  signalRoomSlug: true,
  signalRoomImportEnabled: true,
  lateProfileId: true,
  lateProfileName: true,
  brandVersion: true,
  // Onboarding v2: written only by the dedicated onboarding endpoints, never by generic profile writes.
  instagramHandle: true,
  checklistDismissedAt: true,
  dfyActivationRequestedAt: true,
  houseProfile: true,
  houseProfileRev: true,
  profilcheckGen: true,
  // DFY channel: admin-only (POST /api/admin/companies/:id/dfy-profile).
  dfyProfile: true,
  dfyPlatform: true,
  dfyImageAspectRatio: true,
}).extend({
  website: z.string().transform(val => {
    if (!val || val.trim() === '') return '';
    const trimmed = val.trim();
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return trimmed;
    }
    return `https://${trimmed}`;
  }).pipe(z.string().url("Bitte geben Sie eine gültige Website-Adresse ein").or(z.literal(''))),
  contactEmail: z.string().email("Bitte geben Sie eine gültige E-Mail-Adresse ein"),
  contactPhone: z.string().optional().default(''),
  contactFirstName: z.string().min(1, "Vorname ist erforderlich"),
  contactLastName: z.string().min(1, "Nachname ist erforderlich"),
  address: z.string().optional().default(''),
  // Optional for every company (Hotel-Edition PLAN §1 item 2): "" = no LinkedIn page (the column is NOT NULL).
  linkedinProfile: z.string().trim().url("Bitte geben Sie eine gültige LinkedIn-Adresse ein").or(z.literal('')).optional().default(''),
});

// Separate schema for company profile updates (excludes password fields)
export const updateCompanyProfileSchema = insertCompanySchema.partial().extend({
  name: z.string().min(1, "Firmenname ist erforderlich").optional(),
  industry: z.string().min(1, "Branche ist erforderlich").optional(),
  targetAudience: z.string().min(1, "Zielgruppe ist erforderlich").optional(),
  valueProposition: z.string().min(1, "Wertversprechen ist erforderlich").optional(),
});

// Schema for updating AI-generated marketing fields (Marketing Setup page)
export const updateAIFieldsSchema = z.object({
  businessOverview: z.string().optional(),
  valueProposition: z.string().optional(),
  targetAudience: z.string().optional(),
}).refine(data => {
  // At least one field must be provided
  return data.businessOverview !== undefined || 
         data.valueProposition !== undefined || 
         data.targetAudience !== undefined;
}, { message: "Mindestens ein Feld muss angegeben werden" });

export type UpdateAIFields = z.infer<typeof updateAIFieldsSchema>;

// Password change schema
export const changePasswordSchema = z.object({
  currentPassword: z.string().optional(), // not required when setting password for first time
  newPassword: z.string().min(8, "Passwort muss mindestens 8 Zeichen lang sein")
    .regex(/[A-Z]/, "Passwort muss mindestens einen Großbuchstaben enthalten")
    .regex(/[a-z]/, "Passwort muss mindestens einen Kleinbuchstaben enthalten")
    .regex(/[0-9]/, "Passwort muss mindestens eine Zahl enthalten"),
  confirmPassword: z.string(),
}).refine(data => data.newPassword === data.confirmPassword, {
  message: "Passwörter stimmen nicht überein",
  path: ["confirmPassword"],
});

// Late.dev Profile schemas
export const insertLateProfileSchema = createInsertSchema(lateProfiles).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertLateProfile = z.infer<typeof insertLateProfileSchema>;
export type LateProfile = typeof lateProfiles.$inferSelect;

export const insertAutomationSchema = createInsertSchema(automations).omit({
  id: true,
  userId: true,
  createdAt: true,
  updatedAt: true,
});

export const insertN8nWorkflowSchema = createInsertSchema(n8nWorkflows).omit({
  id: true,
  userId: true,
  createdAt: true,
  updatedAt: true,
});

export const insertContentItemSchema = createInsertSchema(contentItems).omit({
  id: true,
  userId: true,
  createdAt: true,
  updatedAt: true,
});

export const insertSocialMediaGoalSchema = createInsertSchema(socialMediaGoals).omit({
  id: true,
  userId: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  deadline: z.coerce.date(),
  targetValue: z.coerce.number().int().positive(),
  currentValue: z.coerce.number().int().min(0).default(0)
});

export const insertContentUploadSchema = createInsertSchema(contentUploads).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  reviewedAt: true,
  isProcessed: true,
});

export const reviewContentUploadSchema = z.object({
  status: z.enum(["approved", "rejected"]),
  reviewNotes: z.string().optional(),
});

export const insertOauthAppSchema = createInsertSchema(oauthApps).omit({
  id: true,
  userCount: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  platform: z.enum(["facebook", "instagram", "linkedin", "google_business"]),
  maxUsers: z.coerce.number().int().min(25).max(30).default(25), // Enforced at DB level with CHECK constraint (25-30 users per app)
});

export const insertSocialMediaConnectionSchema = createInsertSchema(socialMediaConnections).omit({
  id: true,
  userId: true,
  connectionDate: true,
  lastTokenRefresh: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  platform: z.enum(["facebook", "instagram", "linkedin", "google_business"]),
  tokenExpiry: z.coerce.date().optional(),
});

export const insertGeneratedPostSchema = createInsertSchema(generatedPosts).omit({
  id: true,
  approvedBy: true,
  approvedAt: true,
  postedAt: true,
  autoPostedAt: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  platform: z.enum(["facebook", "instagram", "linkedin", "google_business", "twitter"]),
  scheduledFor: z.coerce.date().optional(),
}).refine(data => {
  // XOR validation: Exactly one of contentUploadId OR sourceId must be set
  const hasContentUpload = !!data.contentUploadId;
  const hasSource = !!data.sourceId;
  return hasContentUpload !== hasSource; // XOR: one must be true, one must be false
}, {
  message: "Exactly one of contentUploadId or sourceId must be provided",
  path: ["contentUploadId"], // Error path for form feedback
}).refine(data => {
  // sourceType must be set when sourceId is provided
  if (data.sourceId && !data.sourceType) {
    return false;
  }
  return true;
}, {
  message: "sourceType is required when sourceId is provided",
  path: ["sourceType"],
});

export const approveGeneratedPostSchema = z.object({
  approvalStatus: z.enum(["approved", "rejected"]),
  approvalNotes: z.string().optional(),
});

export const updateGeneratedPostSchema = z.object({
  postTitle: z.string().optional(),
  postContent: z.string().optional(),
  hashtags: z.array(z.string()).optional(),
});

export const publishPostSchema = z.object({
  isDraft: z.boolean().optional().default(false),
  platform: z.enum(["linkedin", "facebook", "instagram"]).optional(),
  scheduledFor: z.coerce.date().optional(),
});

export const updatePostingStatusSchema = z.object({
  postingStatus: z.enum(["draft", "scheduled", "posting", "posted", "failed"]),
  platformPostId: z.string().optional(),
  postingError: z.string().optional(),
  engagementData: z.record(z.any()).optional(),
  scheduledFor: z.coerce.date().optional(),
});

export const insertEvergreenContentSchema = createInsertSchema(evergreenContent).omit({
  id: true,
  createdAt: true,
}).extend({
  contentType: z.enum(["tip", "behind-scenes", "education", "story"]),
  status: z.enum(["unused", "used", "archived"]).default("unused"),
  relevanceScore: z.coerce.number().int().min(1).max(10).default(5),
});

// Content Pillar schema for validation
const contentPillarSchema = z.object({
  name: z.string(),
  type: z.enum(["context_setting", "capability_building", "solution_supporting"]),
  themes: z.array(z.string()),
  primaryBuyerStage: z.string(),
  why: z.string().optional(),
});

// Message schema for Before/Bridge/After transformations
const messageSchema = z.object({
  title: z.string(),
  before: z.string(),
  bridge: z.string(),
  after: z.string(),
});

export const insertMarketingStrategySchema = createInsertSchema(marketingStrategies).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  generatedAt: true,
}).extend({
  contentPillars: z.array(contentPillarSchema).default([]),
  messages: z.array(messageSchema).default([]),
});

export const updateMarketingStrategySchema = z.object({
  manifesto: z.string().optional(),
  enemyName: z.string().optional(),
  enemyDescription: z.string().optional(),
  povStatement: z.string().optional(),
  positioningStatement: z.string().optional(),
  messages: z.array(messageSchema).optional(),
  contentPillars: z.array(contentPillarSchema).optional(),
  surfaceProblem: z.string().optional(),
  rootCause: z.string().optional(),
  businessImpact: z.string().optional(),
  emotionalImpact: z.string().optional(),
});

export const insertIndustryNewsSchema = createInsertSchema(industryNews).omit({
  id: true,
  reviewedBy: true,
  reviewedAt: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  status: z.enum(["pending", "approved", "rejected"]).default("pending"),
  relevanceScore: z.coerce.number().int().min(0).max(100).optional(),
});

export const insertNapkinWisdomSchema = createInsertSchema(napkinWisdom).omit({
  id: true,
  reviewedBy: true,
  reviewedAt: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  status: z.enum(["pending", "approved", "rejected"]).default("pending"),
  relevanceScore: z.coerce.number().int().min(0).max(100).optional(),
});

export const reviewNapkinWisdomSchema = z.object({
  status: z.enum(["approved", "rejected"]),
  reviewNotes: z.string().optional(),
});

export const insertRepurposingContentSchema = createInsertSchema(repurposingContent).omit({
  id: true,
  reviewedBy: true,
  reviewedAt: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  sourceType: z.enum(["youtube", "article", "blog"]),
  status: z.enum(["pending", "approved", "rejected"]).default("pending"),
  title: z.string().default("Wird verarbeitet..."), // Optional title, will be updated by N8N
});

export const reviewRepurposingContentSchema = z.object({
  status: z.enum(["approved", "rejected"]),
  reviewNotes: z.string().optional(),
});

export const insertLateSocialAccountSchema = createInsertSchema(lateSocialAccounts).omit({
  id: true,
  userId: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  platform: z.string(), // Late.dev supports many platforms
  tokenExpiresAt: z.coerce.date().optional(),
});

// Visual Brand Request schemas
export const insertVisualBrandRequestSchema = createInsertSchema(visualBrandRequests).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  resultImages: true,
  n8nJobId: true,
  n8nError: true,
}).extend({
  mode: z.enum(["person", "product"]),
  status: z.enum(["submitted", "processing", "ready", "failed"]).default("submitted"),
});

export const visualBrandResultImageSchema = z.object({
  id: z.string(),
  filePath: z.string(),
  mimeType: z.string(),
  createdAt: z.string(),
});

// A-Team Briefing - Content DNA Update Schema
const examplePostSchema = z.object({
  url: z.string().url().optional().or(z.literal("")),
  text: z.string().optional(),
}).refine(data => data.url || data.text, {
  message: "Either URL or text is required",
});

const platformPostsSchema = z.object({
  linkedin: z.array(examplePostSchema).optional().default([]),
  instagram: z.array(examplePostSchema).optional().default([]),
  facebook: z.array(examplePostSchema).optional().default([]),
  youtube: z.array(examplePostSchema).optional().default([]),
});

const postingFrequencySchema = z.object({
  linkedin: z.number().int().min(0).max(30).optional().default(0),
  instagram: z.number().int().min(0).max(30).optional().default(0),
  facebook: z.number().int().min(0).max(30).optional().default(0),
  youtube: z.number().int().min(0).max(30).optional().default(0),
});

const contentMixSchema = z.object({
  educational: z.number().int().min(0).max(100).optional().default(0),
  thoughtLeadership: z.number().int().min(0).max(100).optional().default(0),
  behindTheScenes: z.number().int().min(0).max(100).optional().default(0),
  promotional: z.number().int().min(0).max(100).optional().default(0),
}).refine(data => {
  const sum = (data.educational || 0) + (data.thoughtLeadership || 0) + (data.behindTheScenes || 0) + (data.promotional || 0);
  return sum === 100; // Must sum to exactly 100%
}, {
  message: "Content mix percentages must sum to exactly 100%",
});

export const updateContentDnaSchema = z.object({
  // Section 1: Brand Voice (4 slider pairs)
  brandVoiceTones: z.object({
    professional_casual: z.number().min(0).max(100),
    direct_empathetic: z.number().min(0).max(100),
    factual_emotional: z.number().min(0).max(100),
    formal_conversational: z.number().min(0).max(100),
  }),
  // Section 2: Example Posts
  examplePosts: platformPostsSchema,
  // Section 3: Industry & Interests
  specificNiche: z.string().optional(),
  topicsOfInterest: z.array(z.string()).optional().default([]),
  newsletters: z.array(z.string()).optional().default([]),
  topicsToAvoid: z.array(z.string()).optional().default([]),
  topicsToAvoidOther: z.string().optional(),
  // Section 4: Content Strategy
  postingFrequency: postingFrequencySchema.optional(),
  contentMix: contentMixSchema,
  evergreenTopics: z.array(z.string()).optional().default([]),
}).refine(data => {
  // Count total example posts across all platforms
  const totalPosts = 
    (data.examplePosts.linkedin?.length || 0) +
    (data.examplePosts.instagram?.length || 0) +
    (data.examplePosts.facebook?.length || 0) +
    (data.examplePosts.youtube?.length || 0);
  return totalPosts >= 2; // At least 2 posts required
}, {
  message: "At least 2 example posts are required across all platforms",
  path: ["examplePosts"],
});

// Types
export type InsertUser = z.infer<typeof insertUserSchema>;
export type UpsertUser = z.infer<typeof upsertUserSchema>;
export type User = typeof users.$inferSelect;
export type InsertCompany = z.infer<typeof insertCompanySchema>;
export type UpdateCompanyProfile = z.infer<typeof updateCompanyProfileSchema>;
export type ChangePassword = z.infer<typeof changePasswordSchema>;
export type Company = typeof companies.$inferSelect;
export type CompanyProfile = Omit<Company, 'passwordHash'>; // Company data without sensitive fields
export type OnboardingJob = typeof onboardingJobs.$inferSelect;
export type InsertAutomation = z.infer<typeof insertAutomationSchema>;
export type Automation = typeof automations.$inferSelect;
export type InsertN8nWorkflow = z.infer<typeof insertN8nWorkflowSchema>;
export type N8nWorkflow = typeof n8nWorkflows.$inferSelect;
export type InsertContentItem = z.infer<typeof insertContentItemSchema>;
export type ContentItem = typeof contentItems.$inferSelect;
export type InsertSocialMediaGoal = z.infer<typeof insertSocialMediaGoalSchema>;
export type SocialMediaGoal = typeof socialMediaGoals.$inferSelect;
export type InsertContentUpload = z.infer<typeof insertContentUploadSchema>;
export type ReviewContentUpload = z.infer<typeof reviewContentUploadSchema>;
export type ContentUpload = typeof contentUploads.$inferSelect;
export type InsertOauthApp = z.infer<typeof insertOauthAppSchema>;
export type OauthApp = typeof oauthApps.$inferSelect;
export type InsertSocialMediaConnection = z.infer<typeof insertSocialMediaConnectionSchema>;
export type SocialMediaConnection = typeof socialMediaConnections.$inferSelect;
export type InsertGeneratedPost = z.infer<typeof insertGeneratedPostSchema>;
export type GeneratedPost = typeof generatedPosts.$inferSelect;
export type ApproveGeneratedPost = z.infer<typeof approveGeneratedPostSchema>;
export type UpdatePostingStatus = z.infer<typeof updatePostingStatusSchema>;
export type InsertEvergreenContent = z.infer<typeof insertEvergreenContentSchema>;
export type EvergreenContent = typeof evergreenContent.$inferSelect;
export type InsertMarketingStrategy = z.infer<typeof insertMarketingStrategySchema>;
export type UpdateMarketingStrategy = z.infer<typeof updateMarketingStrategySchema>;
export type MarketingStrategy = typeof marketingStrategies.$inferSelect;
export type ContentPillar = { name: string; type: "context_setting" | "capability_building" | "solution_supporting"; themes: string[]; primaryBuyerStage: string; why?: string };
export type StrategyMessage = { title: string; before: string; bridge: string; after: string };
export type InsertIndustryNews = z.infer<typeof insertIndustryNewsSchema>;
export type IndustryNews = typeof industryNews.$inferSelect;
export type InsertNapkinWisdom = z.infer<typeof insertNapkinWisdomSchema>;
export type ReviewNapkinWisdom = z.infer<typeof reviewNapkinWisdomSchema>;
export type NapkinWisdom = typeof napkinWisdom.$inferSelect;
export type InsertRepurposingContent = z.infer<typeof insertRepurposingContentSchema>;
export type ReviewRepurposingContent = z.infer<typeof reviewRepurposingContentSchema>;
export type RepurposingContent = typeof repurposingContent.$inferSelect;
export type InsertLateSocialAccount = z.infer<typeof insertLateSocialAccountSchema>;
export type LateSocialAccount = typeof lateSocialAccounts.$inferSelect;
export type UpdateContentDna = z.infer<typeof updateContentDnaSchema>;
export type InsertVisualBrandRequest = z.infer<typeof insertVisualBrandRequestSchema>;
export type VisualBrandRequest = typeof visualBrandRequests.$inferSelect;
export type VisualBrandResultImage = z.infer<typeof visualBrandResultImageSchema>;

// ── Cockpit slice 2: Lina's weekly briefing ─────────────────────────────────
// One row per (company, ISO week). Claimed by an INSERT … ON CONFLICT DO NOTHING (so a week is
// generated once even when the sweep and the finalizer hook race), then filled with facts +
// prose. `generator` records whether n8n prose arrived or the deterministic template was used.
export const linaWeeklyBriefings = pgTable("lina_weekly_briefings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: varchar("company_id").notNull().references(() => companies.id, { onDelete: "cascade" }),
  weekIso: text("week_iso").notNull(),
  /** "pending" (claimed, not filled yet) | "ready" */
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  /** "n8n" | "template" */
  generator: varchar("generator", { length: 20 }).notNull().default("template"),
  factsJson: jsonb("facts_json").notNull().default({}),
  /** string[] — 2–4 short German paragraphs in Lina's voice */
  paragraphs: jsonb("paragraphs").notNull().default([]),
  signalText: text("signal_text"),
  /** "upload_photos" | "answer_strategy" — null when no task this week */
  taskKind: varchar("task_kind", { length: 40 }),
  taskTitle: text("task_title"),
  taskReason: text("task_reason"),
  /** client CTA: "upload-photo" | "open-strategy" */
  taskAction: varchar("task_action", { length: 40 }),
  taskSkippedAt: timestamp("task_skipped_at"),
  taskDoneAt: timestamp("task_done_at"),
  generatedAt: timestamp("generated_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("uq_lina_weekly_briefings_company_week").on(t.companyId, t.weekIso),
  index("idx_lina_weekly_briefings_week").on(t.weekIso),
]);
export type LinaWeeklyBriefing = typeof linaWeeklyBriefings.$inferSelect;
export type InsertLinaWeeklyBriefing = typeof linaWeeklyBriefings.$inferInsert;

// Hotel-Edition part C (PLAN.md §6.2/§6.8, migration 0030): a hotel's topic wish from the Themenkatalog page.
// Status runs neu → beim_team → eingeplant (monotonic, set by the Signal Room's signed acks); abgelehnt is
// set by the team only. request_key = the client's per-submit key (idempotent retry); request_state = the
// ops-mail state of the SAVED wish ('sending' = mail queued in onboarding_tasks kind topic_wish_mail, 'sent' =
// delivered). The wish is visible to the hotel and the Signal Room in both states (Mike R2, 09.10.).
export const HOTEL_TOPIC_WISH_STATUSES = ["neu", "beim_team", "eingeplant", "abgelehnt"] as const;
export type HotelTopicWishStatus = (typeof HOTEL_TOPIC_WISH_STATUSES)[number];
export const hotelTopicWishes = pgTable("hotel_topic_wishes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: varchar("company_id").notNull().references(() => companies.id, { onDelete: "cascade" }),
  topic: text("topic").notNull(),
  period: text("period"),
  note: text("note"),
  photoUploadIds: jsonb("photo_upload_ids").$type<string[]>().notNull().default([]),
  status: text("status").$type<HotelTopicWishStatus>().notNull().default("neu"),
  requestKey: text("request_key").notNull(),
  requestState: text("request_state").$type<"sending" | "sent">().notNull().default("sending"),
  createdBy: varchar("created_by").references(() => users.id, { onDelete: "set null" }),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  index("idx_hotel_topic_wishes_company_created").on(t.companyId, t.createdAt, t.id),
  uniqueIndex("uq_hotel_topic_wishes_company_request").on(t.companyId, t.requestKey),
  check("hotel_topic_wishes_status_check", sql`${t.status} IN ('neu', 'beim_team', 'eingeplant', 'abgelehnt')`),
  check("hotel_topic_wishes_request_state_check", sql`${t.requestState} IN ('sending', 'sent')`),
]);
export type HotelTopicWish = typeof hotelTopicWishes.$inferSelect;
export type InsertHotelTopicWish = typeof hotelTopicWishes.$inferInsert;

// Hotel video upload (PLAN.md §C, migration 0033). Separate from content_uploads on purpose: every photo path
// (n8n photo workflow, brand references, Signal-Room /photos, mediaPolicy) reads that table and would mistreat a
// video. Status machine and fences in PLAN §13.
export const HOTEL_VIDEO_STATUSES = [
  "reserved", "uploading", "completing", "uploaded", "processing", "ready", "failed", "rejected", "aborted", "deleting",
] as const;
export type HotelVideoStatus = (typeof HOTEL_VIDEO_STATUSES)[number];

export const hotelVideoUploads = pgTable("hotel_video_uploads", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  companyId: varchar("company_id").notNull().references(() => companies.id, { onDelete: "cascade" }),
  userId: varchar("user_id").notNull().references(() => users.id, { onDelete: "cascade" }),
  uploadMethod: text("upload_method").$type<"direct" | "token">().notNull(),
  clientUploadId: text("client_upload_id").notNull(),
  status: text("status").$type<HotelVideoStatus>().notNull().default("reserved"),
  generation: integer("generation").notNull().default(1),
  originalKey: text("original_key"),
  s3UploadId: text("s3_upload_id"),
  declaredSize: bigint("declared_size", { mode: "number" }).notNull(),
  declaredMime: text("declared_mime").notNull(),
  originalName: text("original_name").notNull(),
  partSize: integer("part_size").notNull(),
  partCount: integer("part_count").notNull(),
  message: text("message"),
  pillarName: text("pillar_name"),
  consentConfirmedAt: timestamp("consent_confirmed_at"),
  durationMs: integer("duration_ms"),
  width: integer("width"),
  height: integer("height"),
  compressedKey: text("compressed_key"),
  compressedBytes: bigint("compressed_bytes", { mode: "number" }),
  posterKey: text("poster_key"),
  outputPrefix: text("output_prefix"),
  attempts: integer("attempts").notNull().default(0),
  leaseOwner: varchar("lease_owner"),
  leaseUntil: timestamp("lease_until"),
  errorCode: text("error_code"),
  errorMessage: text("error_message"),
  deleteRequestedAt: timestamp("delete_requested_at"),
  stateChangedAt: timestamp("state_changed_at").defaultNow().notNull(),
  uploadedAt: timestamp("uploaded_at"),
  readyAt: timestamp("ready_at"),
  originalDeletedAt: timestamp("original_deleted_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
}, (t) => [
  uniqueIndex("uq_hotel_video_uploads_company_client").on(t.companyId, t.clientUploadId),
  index("idx_hotel_video_uploads_status_uploaded").on(t.status, t.uploadedAt),
  index("idx_hotel_video_uploads_company_created").on(t.companyId, t.createdAt),
  index("idx_hotel_video_uploads_ready").on(t.readyAt, t.id),
  check("hotel_video_uploads_status_check", sql`${t.status} IN ('reserved', 'uploading', 'completing', 'uploaded', 'processing', 'ready', 'failed', 'rejected', 'aborted', 'deleting')`),
  check("hotel_video_uploads_method_check", sql`${t.uploadMethod} IN ('direct', 'token')`),
  check("hotel_video_uploads_size_check", sql`${t.declaredSize} > 0 AND ${t.partSize} > 0 AND ${t.partCount} BETWEEN 1 AND 10000`),
]);
export type HotelVideoUpload = typeof hotelVideoUploads.$inferSelect;
export type InsertHotelVideoUpload = typeof hotelVideoUploads.$inferInsert;

export type HotelVideoActor = "user" | "token" | "worker" | "reconciler" | "retention";

// Audit per transition (PLAN §11a). No FK: it outlives the deleted video. Never tokens, signed URLs or bytes.
export const hotelVideoEvents = pgTable("hotel_video_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  videoId: varchar("video_id").notNull(),
  companyId: varchar("company_id").notNull(),
  actor: text("actor").$type<HotelVideoActor>().notNull(),
  actorUserId: varchar("actor_user_id"),
  fromStatus: text("from_status"),
  toStatus: text("to_status"),
  generation: integer("generation"),
  reason: text("reason"),
  outcome: text("outcome").notNull().default("ok"),
  at: timestamp("at").defaultNow().notNull(),
}, (t) => [
  index("idx_hotel_video_events_video").on(t.videoId, t.at),
  index("idx_hotel_video_events_at").on(t.at),
  check("hotel_video_events_actor_check", sql`${t.actor} IN ('user', 'token', 'worker', 'reconciler', 'retention')`),
]);
export type HotelVideoEvent = typeof hotelVideoEvents.$inferSelect;

// Last success/error per background loop (PLAN §40b): worker, retention, reconciler.
export const hotelVideoHeartbeats = pgTable("hotel_video_heartbeats", {
  name: text("name").primaryKey(),
  lastSuccessAt: timestamp("last_success_at"),
  lastErrorAt: timestamp("last_error_at"),
  details: jsonb("details").$type<Record<string, unknown>>().notNull().default({}),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});
